import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import AddressAutocomplete from '../components/common/AddressAutocomplete';
import ConfirmModal from '../components/common/ConfirmModal';
import { useAuth } from '../context/AuthContext';
import { getUsers, getCustomLists, updateCustomList, updateProviderServices, getDeletionRequests, approveDeletion, denyDeletion, uploadLogo, removeLogo, getWorkflows, createWorkflow, updateWorkflow, deleteWorkflow, toggleWorkflow, duplicateWorkflow, resetUserPassword, pauseUser, unpauseUser, changeAccessLevel } from '../utils/api';
import api from '../utils/api';
import { Mail, UserX, UserCheck, Crown, Sun, Moon, Plus, Trash2, Save, Check, X, AlertTriangle, Upload, Image, Play, Pause, Copy, ChevronDown, ChevronRight, Zap, Clock, MessageSquare, UserPlus, Users, FileText, GitBranch, ArrowDown, ArrowUp, Pencil, ToggleLeft, ToggleRight, Timer, Key, Lock, Unlock , ListFilter } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatPhone } from '../utils/helpers';

import { ASSIGNABLE_ROLES as ROLES, ROLE_LABELS, ROLE_DESCRIPTIONS, ROLE_COLORS, ROLE_DEFAULT_ACCESS, getRoleLabel, getRoleBadgeStyle } from '../utils/roles';


// Each entry can be a list item OR a section header (isHeader: true)
const LIST_DEFINITIONS = [
  // ── Case Detail ──────────────────────────────────────────────────────────────
  { isHeader: true, label: 'Case Detail' },
  { key: 'case_status_options',   label: 'Case Status',              desc: 'Status dropdown in the case detail header (e.g. Active, Settled, Closed)' },
  { key: 'legal_status_options',  label: 'Legal Status',             desc: 'Legal Information → Status field' },
  { key: 'legal_type_options',    label: 'Legal / Claim Type',       desc: 'Legal Information → Case Type field (WC, Third Party, etc.)' },
  { key: 'af_fee_options',        label: 'AF Fee %',                 desc: 'Legal Information → Attorney Fee percentage dropdown' },
  { key: 'benefit_type_options',  label: 'Benefit Types',            desc: 'Benefit Information → Benefit Type dropdown' },
  { key: 'body_parts',            label: 'Body Parts',               desc: 'Claim Information → Injured Body Parts multi-select' },
  { key: 'note_type_options',     label: 'Note Types',               desc: 'Notes tab → Note Type dropdown when adding a case note' },
  // ── Treatment Tab ────────────────────────────────────────────────────────────
  { isHeader: true, label: 'Treatment Tab' },
  { key: 'treatment_type_options', label: 'Treatment / Appointment Types', desc: 'Treatment tab → Appointment Type dropdown and Calendar Treatment events' },
  // ── Client Intake ────────────────────────────────────────────────────────────
  { isHeader: true, label: 'Client Intake' },
  { key: 'language_options',      label: 'Languages',                desc: 'Intake form → Claimant Language multi-select' },
  { key: 'comm_pref_options',     label: 'Communication Preferences', desc: 'Intake form → Preferred Contact Method multi-select' },
  // ── Provider Network ─────────────────────────────────────────────────────────
  { isHeader: true, label: 'Provider Network' },
  { key: 'provider_type_options', label: 'Provider Types',           desc: 'Provider Network → Type dropdown when adding or editing a provider' },
  { key: 'specialty_options',     label: 'Provider Specialties',     desc: 'Provider Network → Specialty dropdown when adding or editing a provider' },
  // ── Documents & Templates ────────────────────────────────────────────────────
  { isHeader: true, label: 'Documents & Templates' },
  { key: 'template_category_options', label: 'Template Categories',  desc: 'Letters & Forms → Category dropdown when creating a document template' },
];

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState('team');
  const [team, setTeam] = useState([]);
  const [seats, setSeats] = useState({});
  const [invites, setInvites] = useState([]);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'case_manager', access_level: 'standard' });
  const [loading, setLoading] = useState(true);
  const [sub,          setSub]          = useState({});


  const userAccess = user?.access_level || 'standard';
  const isSuperAdmin = userAccess === 'super_admin';
  const isAdminAccess = ['super_admin', 'admin'].includes(userAccess);
  // Keep legacy name for backward compat in UI (controls team mgmt, deletion requests, firm settings)
  const isOwnerOrAdmin = isAdminAccess;

  // Custom Lists
  const [customLists, setCustomLists] = useState({});
  const [editingList, setEditingList] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [newItem, setNewItem] = useState('');
  const [savingList, setSavingList] = useState(false);
  const [editLayoutMap, setEditLayoutMap] = useState({});  // legal type → layout map
  const [editSolRules, setEditSolRules] = useState({});  // legal type → { years, state }

  // Services Offered by Provider Type
  const [selectedProviderType, setSelectedProviderType] = useState(null);
  const [newService, setNewService] = useState('');
  const [savingServices, setSavingServices] = useState(false);
  // Always tracks the most current provider type list — updated on load AND on every edit
  const [liveProviderTypes, setLiveProviderTypes] = useState([]);

  // Super Admin: reset password
  const [resetPwdUser, setResetPwdUser] = useState(null);
  const [resetPwdValue, setResetPwdValue] = useState('');

  // Deletion Requests
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  // Firm settings form (editable)
  const [firmForm, setFirmForm] = useState({
    name: '',
    address: '',
    phone: '',
    fax: '',
    email: '',
    website: ''
  });
  const [firmSaving, setFirmSaving] = useState(false);
  const logoInputRef = useRef(null);
  const [logoUploading, setLogoUploading] = useState(false);

  // Theme
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('zeus-theme') || 'light';
  });

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('zeus-theme', next);
    document.documentElement.setAttribute('data-theme', next);
  };

  // ── Workflows ──
  const [workflows, setWorkflows] = useState([]);
  const [wfLoading, setWfLoading] = useState(false);
  const [wfMode, setWfMode] = useState('list'); // 'list' | 'editor'
  const [wfEditing, setWfEditing] = useState(null); // workflow being edited
  const [confirmAction, setConfirmAction] = useState(null);
  const [wfForm, setWfForm] = useState({
    name: '', description: '', trigger_type: 'case_created', trigger_config: {}, steps: [], is_active: true,
  });
  const [wfSaving, setWfSaving] = useState(false);

  const TRIGGER_TYPES = [
    { value: 'case_created', label: 'Case Created', icon: <Zap size={14} />, desc: 'Runs when a new case is created' },
    { value: 'status_changed', label: 'Status Changed', icon: <GitBranch size={14} />, desc: 'Runs when case status changes' },
    { value: 'field_changed', label: 'Field Changed', icon: <Pencil size={14} />, desc: 'Runs when a specific field is updated' },
    { value: 'provider_added', label: 'Provider Added', icon: <UserPlus size={14} />, desc: 'Runs when a treating provider is added to a case' },
    { value: 'condition_check', label: 'Condition Check', icon: <GitBranch size={14} />, desc: 'If field(s) match a condition (filled, blank, equals...)' },
    { value: 'timed_condition', label: 'Timed Condition', icon: <Timer size={14} />, desc: 'If a field has been in a state for X business days' },
    { value: 'manual', label: 'Manual Trigger', icon: <Play size={14} />, desc: 'Run manually from a case' },
  ];

  const ACTION_TYPES = [
    { value: 'change_status', label: 'Change Status', icon: <GitBranch size={14} />, color: '#3b82f6' },
    { value: 'add_note', label: 'Add Note', icon: <MessageSquare size={14} />, color: '#22c55e' },
    { value: 'send_notification', label: 'Send Notification', icon: <Mail size={14} />, color: '#f59e0b' },
    { value: 'assign_user', label: 'Assign User', icon: <UserPlus size={14} />, color: '#a78bfa' },
    { value: 'assign_role', label: 'Assign to Role', icon: <Users size={14} />, color: '#8b5cf6' },
    { value: 'create_task', label: 'Create Task', icon: <FileText size={14} />, color: '#ec4899' },
    { value: 'wait', label: 'Wait / Delay', icon: <Clock size={14} />, color: '#64748b' },
    { value: 'condition', label: 'If / Then / Else', icon: <GitBranch size={14} />, color: '#06b6d4' },
  ];

  const STATUS_OPTIONS = customLists.case_status_options || ['intake','active','pending','closed','settled','disengage'];
  const ROLE_OPTIONS_WF = ['owner','attorney','paralegal','staff','admin'];

  const ALL_FIELDS = [
    { group: 'Claimant', fields: [
      { value: 'client_name', label: 'Claimant Name' },
      { value: 'address', label: 'Address' },
      { value: 'mailing_address', label: 'Mailing Address' },
      { value: 'phone', label: 'Phone' },
      { value: 'phone_alt', label: 'Alt Phone' },
      { value: 'email', label: 'Email' },
      { value: 'ssn', label: 'SSN' },
      { value: 'county', label: 'County' },
      { value: 'date_of_birth', label: 'Date of Birth' },
      { value: 'date_of_incident', label: 'Date of Incident' },
      { value: 'claim_number', label: 'Claim Number' },
      { value: 'dwc_number', label: 'DWC Number' },
      { value: 'sex', label: 'Sex' },
      { value: 'language', label: 'Language' },
    ]},
    { group: 'Case Info', fields: [
      { value: 'status', label: 'Case Status' },
      { value: 'case_type', label: 'Case Type' },
      { value: 'claim_type', label: 'Claim Type' },
      { value: 'claim_status', label: 'Claim Status' },
      { value: 'body_parts', label: 'Body Parts' },
      { value: 'mechanism_of_injury', label: 'Mechanism of Injury' },
      { value: 'compensable', label: 'Compensable' },
      { value: 'disputes', label: 'Disputes' },
      { value: 'demand_amount', label: 'Demand Amount' },
      { value: 'settlement_amount', label: 'Settlement Amount' },
    ]},
    { group: 'Legal', fields: [
      { value: 'assigned_attorney', label: 'Assigned Attorney' },
      { value: 'referred_by', label: 'Referred By' },
      { value: 'af_fee', label: 'AF Fee' },
      { value: 'screener', label: 'Screener' },
      { value: 'consult_date', label: 'Consult Date' },
      { value: 'lor_faxed', label: 'LOR Faxed Date' },
      { value: 'initial_billing_done', label: 'Initial Billing Done' },
      { value: 'attorney_notes_status', label: 'Attorney Notes' },
    ]},
    { group: 'Insurance', fields: [
      { value: 'insurance.insurance_company', label: 'Insurance Company' },
      { value: 'insurance.network', label: 'Provider Network' },
      { value: 'insurance.adjuster_name', label: 'Adjuster Name' },
      { value: 'insurance.adjuster_email', label: 'Adjuster Email' },
      { value: 'insurance.adjuster_phone', label: 'Adjuster Phone' },
      { value: 'insurance.claim_number', label: 'Insurance Claim #' },
      { value: 'insurance.policy_number', label: 'Policy #' },
    ]},
    { group: 'Benefits', fields: [
      { value: 'benefit_type', label: 'Benefit Type' },
      { value: 'work_status_dwc73', label: 'Work Status' },
      { value: 'aww', label: 'AWW' },
      { value: 'benefit_rate_percent', label: '70% of AWW' },
      { value: 'actual_benefit_amount', label: 'Actual Benefit Amt' },
      { value: 'attorney_fees', label: 'Attorney Fees' },
      { value: 'rec_benefits', label: 'Receiving Benefits' },
    ]},
    { group: 'Employer', fields: [
      { value: 'employer.company', label: 'Employer Company' },
      { value: 'employer.address', label: 'Employer Address' },
      { value: 'employer.phone', label: 'Employer Phone' },
    ]},
    { group: 'Treating Doctors', fields: [
      { value: 'preceding_td.doctor_name', label: 'Preceding TD Doctor' },
      { value: 'preceding_td.clinic', label: 'Preceding TD Clinic' },
      { value: 'referred_td.doctor_name', label: 'Referred TD Doctor' },
      { value: 'referred_td.clinic', label: 'Referred TD Clinic' },
    ]},
    { group: 'Deceased', fields: [
      { value: 'date_of_death', label: 'Date of Death' },
      { value: 'cause_of_death', label: 'Cause of Death' },
      { value: 'beneficiary_name', label: 'Beneficiary Name' },
      { value: 'beneficiary_relationship', label: 'Beneficiary Relationship' },
      { value: 'beneficiary_phone', label: 'Beneficiary Phone' },
    ]},
  ];

  const OPERATORS = [
    { value: 'is_filled', label: 'Is Filled', needsValue: false },
    { value: 'is_blank', label: 'Is Blank', needsValue: false },
    { value: 'equals', label: 'Equals', needsValue: true },
    { value: 'not_equals', label: 'Does Not Equal', needsValue: true },
    { value: 'contains', label: 'Contains', needsValue: true },
    { value: 'not_contains', label: 'Does Not Contain', needsValue: true },
    { value: 'greater_than', label: 'Greater Than', needsValue: true },
    { value: 'less_than', label: 'Less Than', needsValue: true },
    { value: 'starts_with', label: 'Starts With', needsValue: true },
  ];

  const FLAT_FIELD_OPTIONS = ALL_FIELDS.flatMap(g => g.fields);

  // Returns dropdown options for fields that have known value sets
  const getFieldValueOptions = (fieldValue) => {
    if (!fieldValue) return null;
    // Attorneys / staff lookups from team
    const attorneys = team.filter(u => u.role === 'attorney' && u.is_active).map(u => ({ value: u._id, label: `${u.first_name} ${u.last_name}` }));
    const allStaff = team.filter(u => u.is_active).map(u => ({ value: u._id, label: `${u.first_name} ${u.last_name}` }));

    const fieldOptionsMap = {
      'status':               (customLists.case_status_options || STATUS_OPTIONS).map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) })),
      'claim_type':           (customLists.legal_type_options || []).map(s => ({ value: s, label: s })),
      'claim_status':         (customLists.legal_status_options || []).map(s => ({ value: s, label: s })),
      'af_fee':               (customLists.af_fee_options || []).map(s => ({ value: s, label: s })),
      'benefit_type':         (customLists.benefit_type_options || []).map(s => ({ value: s, label: s })),
      'body_parts':           (customLists.body_parts || []).map(s => ({ value: s, label: s })),
      'assigned_attorney':    attorneys,
      'screener':             allStaff,
      'referred_by':          allStaff,
      'sex':                  [{ value: 'Male', label: 'Male' }, { value: 'Female', label: 'Female' }, { value: 'Other', label: 'Other' }],
      'language':             (customLists.language_options || ['English','Spanish','Other']).map(s => ({ value: s, label: s })),
      'compensable':          [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }, { value: 'Pending', label: 'Pending' }],
      'rec_benefits':         [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }],
      'initial_billing_done': [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }],
      'work_status_dwc73':    [{ value: 'Working', label: 'Working' }, { value: 'Not Working', label: 'Not Working' }, { value: 'Modified Duty', label: 'Modified Duty' }, { value: 'Released', label: 'Released' }],
    };
    return fieldOptionsMap[fieldValue] || null;
  };

  const loadWorkflows = async () => {
    setWfLoading(true);
    try {
      const res = await getWorkflows();
      setWorkflows(Array.isArray(res) ? res : []);
    } catch { setWorkflows([]); }
    finally { setWfLoading(false); }
  };

  const openNewWorkflow = () => {
    setWfEditing(null);
    setWfForm({ name: '', description: '', trigger_type: 'case_created', trigger_config: {}, steps: [], is_active: true });
    setWfMode('editor');
  };

  const openEditWorkflow = (wf) => {
    setWfEditing(wf);
    setWfForm({
      name: wf.name || '', description: wf.description || '',
      trigger_type: wf.trigger_type || 'case_created',
      trigger_config: wf.trigger_config || {},
      steps: (wf.steps || []).map(s => ({ action_type: s.action_type, config: { ...s.config } })),
      is_active: wf.is_active !== false,
    });
    setWfMode('editor');
  };

  const handleSaveWorkflow = async () => {
    if (!wfForm.name?.trim()) return toast.error('Workflow name is required');
    if (wfForm.steps.length === 0) return toast.error('Add at least one step');
    setWfSaving(true);
    try {
      if (wfEditing) {
        await updateWorkflow(wfEditing._id, wfForm);
        toast.success('Workflow updated');
      } else {
        await createWorkflow(wfForm);
        toast.success('Workflow created');
      }
      setWfMode('list');
      loadWorkflows();
    } catch (err) { toast.error(err?.message || 'Failed to save workflow'); }
    finally { setWfSaving(false); }
  };

  const handleDeleteWorkflow = async (wf) => {
    setConfirmAction({
      title: 'Delete Workflow',
      message: `Delete "${wf.name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try { await deleteWorkflow(wf._id); toast.success('Workflow deleted'); loadWorkflows(); }
        catch { toast.error('Failed to delete'); }
      },
    });
  };

  const handleToggleWorkflow = async (wf) => {
    try { const res = await toggleWorkflow(wf._id); setWorkflows(prev => prev.map(w => w._id === wf._id ? res : w)); }
    catch { toast.error('Failed to toggle'); }
  };

  const handleDuplicateWorkflow = async (wf) => {
    try { await duplicateWorkflow(wf._id); toast.success('Workflow duplicated'); loadWorkflows(); }
    catch { toast.error('Failed to duplicate'); }
  };

  const addStep = (action_type) => {
    const defaults = {
      change_status: { new_status: 'active' },
      add_note: { content: '', note_type: 'General', color: '' },
      send_notification: { to_role: 'attorney', message: '' },
      assign_user: { assign_to_user: '' },
      assign_role: { assign_to_role: 'attorney' },
      create_task: { title: '', description: '', due_in_days: 7, assign_to_role: 'paralegal' },
      wait: { days: 1 },
      condition: { logic: 'and', rules: [{ field: '', operator: 'is_filled', value: '' }], then_actions: [{ action_type: 'create_task', config: { title: '', due_in_days: 7, assign_to_role: 'paralegal' } }], else_actions: [] },
    };
    setWfForm(prev => ({ ...prev, steps: [...prev.steps, { action_type, config: defaults[action_type] || {} }] }));
  };

  const updateStep = (idx, config) => {
    setWfForm(prev => ({
      ...prev,
      steps: prev.steps.map((s, i) => i === idx ? { ...s, config: { ...s.config, ...config } } : s),
    }));
  };

  const removeStep = (idx) => {
    setWfForm(prev => ({ ...prev, steps: prev.steps.filter((_, i) => i !== idx) }));
  };

  const moveStep = (idx, dir) => {
    setWfForm(prev => {
      const steps = [...prev.steps];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= steps.length) return prev;
      [steps[idx], steps[newIdx]] = [steps[newIdx], steps[idx]];
      return { ...prev, steps };
    });
  };

  // ── Condition rules builder helper ──
  const renderConditionRules = (rules, logic, onChangeRules, onChangeLogic) => (
    <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'2px' }}>
        <label style={{ fontSize:'0.72rem', color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em' }}>Match</label>
        <button onClick={() => onChangeLogic('and')}
          style={{ padding:'2px 10px', fontSize:'0.72rem', fontWeight:700, borderRadius:'10px', cursor:'pointer', border:'1px solid', transition:'all 0.15s',
            background: logic === 'and' ? 'rgba(34,197,94,0.15)' : 'transparent', color: logic === 'and' ? '#22c55e' : 'var(--text-muted)', borderColor: logic === 'and' ? '#22c55e' : 'var(--zeus-border)',
          }}>ALL (AND)</button>
        <button onClick={() => onChangeLogic('or')}
          style={{ padding:'2px 10px', fontSize:'0.72rem', fontWeight:700, borderRadius:'10px', cursor:'pointer', border:'1px solid', transition:'all 0.15s',
            background: logic === 'or' ? 'rgba(249,115,22,0.15)' : 'transparent', color: logic === 'or' ? '#f97316' : 'var(--text-muted)', borderColor: logic === 'or' ? '#f97316' : 'var(--zeus-border)',
          }}>ANY (OR)</button>
      </div>
      {rules.map((rule, ri) => {
        const op = OPERATORS.find(o => o.value === rule.operator);
        return (
          <div key={ri} style={{ display:'flex', gap:'6px', alignItems:'center' }}>
            {ri > 0 && <span style={{ fontSize:'0.68rem', fontWeight:700, color: logic === 'and' ? '#22c55e' : '#f97316', minWidth:'30px', textAlign:'center' }}>{logic === 'and' ? 'AND' : 'OR'}</span>}
            {ri === 0 && <span style={{ minWidth:'30px' }} />}
            <select className="form-select" style={{ flex:2, fontSize:'0.78rem', padding:'4px 6px' }}
              value={rule.field || ''} onChange={(e) => { const nr = [...rules]; nr[ri] = { ...nr[ri], field: e.target.value, value: '' }; onChangeRules(nr); }}
            >
              <option value="">Select field...</option>
              {ALL_FIELDS.map(g => (
                <optgroup key={g.group} label={g.group}>
                  {g.fields.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </optgroup>
              ))}
            </select>
            <select className="form-select" style={{ flex:1.2, fontSize:'0.78rem', padding:'4px 6px' }}
              value={rule.operator || 'is_filled'} onChange={(e) => { const nr = [...rules]; nr[ri] = { ...nr[ri], operator: e.target.value }; onChangeRules(nr); }}
            >
              {OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {op?.needsValue !== false && (() => {
              const valOpts = getFieldValueOptions(rule.field);
              if (valOpts && valOpts.length > 0) {
                return (
                  <select className="form-select" style={{ flex:1.5, fontSize:'0.78rem', padding:'4px 6px' }}
                    value={rule.value || ''} onChange={(e) => { const nr = [...rules]; nr[ri] = { ...nr[ri], value: e.target.value }; onChangeRules(nr); }}
                  >
                    <option value="">Select...</option>
                    {valOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                );
              }
              return (
                <input className="form-input" style={{ flex:1.5, fontSize:'0.78rem', padding:'4px 6px' }}
                  placeholder="Value..." value={rule.value || ''} onChange={(e) => { const nr = [...rules]; nr[ri] = { ...nr[ri], value: e.target.value }; onChangeRules(nr); }}
                />
              );
            })()}
            {!op?.needsValue && <div style={{ flex:1.5 }} />}
            <button className="btn btn-ghost btn-sm" style={{ padding:'3px', color:'var(--status-red)', flexShrink:0 }} onClick={() => { if (rules.length > 1) onChangeRules(rules.filter((_, i) => i !== ri)); }}>
              <X size={12} />
            </button>
          </div>
        );
      })}
      <button className="btn btn-ghost btn-sm" onClick={() => onChangeRules([...rules, { field: '', operator: 'is_filled', value: '' }])}
        style={{ display:'inline-flex', alignItems:'center', gap:'4px', color:'var(--zeus-gold)', fontSize:'0.78rem', width:'fit-content', marginTop:'2px' }}>
        <Plus size={12} /> Add Rule
      </button>
    </div>
  );

  // ── Mini action builder for then/else branches ──
  const BRANCH_ACTIONS = ACTION_TYPES.filter(a => a.value !== 'condition' && a.value !== 'wait');

  const renderBranchActions = (actions, onChange, label, color) => (
    <div style={{ padding:'10px 12px', background:`${color}08`, border:`1px solid ${color}22`, borderRadius:'var(--radius-sm)', marginTop:'6px' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' }}>
        <label style={{ fontSize:'0.72rem', fontWeight:700, color, textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}</label>
      </div>
      {actions.map((act, ai) => {
        const ad = ACTION_TYPES.find(a => a.value === act.action_type);
        return (
          <div key={ai} style={{ padding:'8px 10px', background:'var(--zeus-navy-light)', borderRadius:'var(--radius-sm)', marginBottom:'6px', border:'1px solid var(--zeus-border)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'6px' }}>
              <span style={{ color: ad?.color || '#64748b', display:'flex', alignItems:'center', gap:'4px', fontSize:'0.8rem', fontWeight:600 }}>{ad?.icon} {ad?.label}</span>
              <select className="form-select" style={{ fontSize:'0.75rem', padding:'2px 6px', width:'auto', marginLeft:'auto' }}
                value={act.action_type} onChange={(e) => { const na = [...actions]; na[ai] = { action_type: e.target.value, config: {} }; onChange(na); }}
              >
                {BRANCH_ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
              <button className="btn btn-ghost btn-sm" style={{ padding:'2px', color:'var(--status-red)' }} onClick={() => onChange(actions.filter((_,i)=>i!==ai))}><X size={11}/></button>
            </div>
            {act.action_type === 'change_status' && (
              <select className="form-select" style={{ fontSize:'0.78rem', padding:'4px 6px' }} value={act.config.new_status || ''} onChange={(e) => { const na=[...actions]; na[ai]={...na[ai],config:{...na[ai].config,new_status:e.target.value}}; onChange(na); }}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
            )}
            {act.action_type === 'add_note' && (
              <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
                <select className="form-select" style={{ fontSize:'0.78rem', padding:'4px 6px' }} value={act.config.note_type||'General'} onChange={(e)=>{const na=[...actions]; na[ai]={...na[ai],config:{...na[ai].config,note_type:e.target.value}}; onChange(na);}}>
                  {(customLists.note_type_options || ['General','Phone Call','Email','Follow-Up','Internal']).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <input className="form-input" style={{ fontSize:'0.78rem', padding:'4px 6px' }} placeholder="Note content..." value={act.config.content||''} onChange={(e)=>{const na=[...actions]; na[ai]={...na[ai],config:{...na[ai].config,content:e.target.value}}; onChange(na);}} />
              </div>
            )}
            {act.action_type === 'send_notification' && (
              <div style={{ display:'flex', gap:'6px' }}>
                <select className="form-select" style={{ flex:1, fontSize:'0.78rem', padding:'4px 6px' }} value={act.config.to_role||'attorney'} onChange={(e)=>{const na=[...actions]; na[ai]={...na[ai],config:{...na[ai].config,to_role:e.target.value}}; onChange(na);}}>
                  {ROLE_OPTIONS_WF.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}
                </select>
                <input className="form-input" style={{ flex:2, fontSize:'0.78rem', padding:'4px 6px' }} placeholder="Message..." value={act.config.message||''} onChange={(e)=>{const na=[...actions]; na[ai]={...na[ai],config:{...na[ai].config,message:e.target.value}}; onChange(na);}} />
              </div>
            )}
            {act.action_type === 'assign_user' && (
              <select className="form-select" style={{ fontSize:'0.78rem', padding:'4px 6px' }} value={act.config.assign_to_user||''} onChange={(e)=>{const na=[...actions]; na[ai]={...na[ai],config:{...na[ai].config,assign_to_user:e.target.value}}; onChange(na);}}>
                <option value="">Select user...</option>
                {team.filter(u => u.is_active).map(u => <option key={u._id} value={u._id}>{u.first_name} {u.last_name} ({u.role})</option>)}
              </select>
            )}
            {act.action_type === 'assign_role' && (
              <select className="form-select" style={{ fontSize:'0.78rem', padding:'4px 6px' }} value={act.config.assign_to_role||'attorney'} onChange={(e)=>{const na=[...actions]; na[ai]={...na[ai],config:{...na[ai].config,assign_to_role:e.target.value}}; onChange(na);}}>
                {ROLE_OPTIONS_WF.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}
              </select>
            )}
            {act.action_type === 'create_task' && (
              <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
                <input className="form-input" style={{ fontSize:'0.78rem', padding:'4px 6px' }} placeholder="Task title..." value={act.config.title||''} onChange={(e)=>{const na=[...actions]; na[ai]={...na[ai],config:{...na[ai].config,title:e.target.value}}; onChange(na);}} />
                <div style={{ display:'flex', gap:'6px' }}>
                  <div style={{ flex:1 }}>
                    <label style={{ fontSize:'0.68rem', color:'var(--text-muted)' }}>Due in days</label>
                    <input className="form-input" type="number" style={{ fontSize:'0.78rem', padding:'4px 6px' }} value={act.config.due_in_days??7} onChange={(e)=>{const na=[...actions]; na[ai]={...na[ai],config:{...na[ai].config,due_in_days:parseInt(e.target.value)||0}}; onChange(na);}} />
                  </div>
                  <div style={{ flex:1 }}>
                    <label style={{ fontSize:'0.68rem', color:'var(--text-muted)' }}>Assign to</label>
                    <select className="form-select" style={{ fontSize:'0.78rem', padding:'4px 6px' }} value={act.config.assign_to_role||'paralegal'} onChange={(e)=>{const na=[...actions]; na[ai]={...na[ai],config:{...na[ai].config,assign_to_role:e.target.value}}; onChange(na);}}>
                      {ROLE_OPTIONS_WF.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
      <button className="btn btn-ghost btn-sm" onClick={() => onChange([...actions, { action_type: 'create_task', config: { title:'', due_in_days:7, assign_to_role:'paralegal' } }])}
        style={{ display:'inline-flex', alignItems:'center', gap:'4px', color, fontSize:'0.75rem', width:'fit-content' }}>
        <Plus size={11} /> Add Action
      </button>
    </div>
  );

  useEffect(() => { loadTeam(); loadCustomListsData(); loadWorkflows(); if (isOwnerOrAdmin) loadDeletionRequests(); }, []);

  // Keep firm form synced from current profile (org)
  useEffect(() => {
    const org = user?.organization || {};
    setFirmForm({
      name: org.name || '',
      address: org.address || '',
      phone: org.phone || '',
      fax: org.fax || '',
      email: org.email || '',
      website: org.website || ''
    });
  }, [user]);

  const loadDeletionRequests = async () => {
    setLoadingRequests(true);
    try {
      const reqs = await getDeletionRequests();
      setDeletionRequests(Array.isArray(reqs) ? reqs : []);
    } catch { setDeletionRequests([]); }
    finally { setLoadingRequests(false); }
  };

  const handleApprove = async (reqId) => {
    setProcessingId(reqId);
    try {
      await approveDeletion(reqId);
      toast.success('Case permanently deleted');
      await loadDeletionRequests();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to approve');
    } finally { setProcessingId(null); }
  };

  const handleDeny = async (reqId) => {
    setProcessingId(reqId);
    try {
      await denyDeletion(reqId);
      toast.success('Deletion request denied');
      await loadDeletionRequests();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to deny');
    } finally { setProcessingId(null); }
  };

  const loadCustomListsData = async () => {
    try {
      const lists = await getCustomLists();
      if (lists && typeof lists === 'object') {
        // Auto-seed any provider type missing from provider_services so they show immediately
        // Use defaults merged with saved — same as the editor
        const DEFAULT_PROVIDER_TYPES = ['Doctor','Specialist','Physical Therapy','Chiropractor','Hospital','Lab','Imaging'];
        const savedTypes = lists.provider_type_options || [];
        const allTypes = [
          ...DEFAULT_PROVIDER_TYPES,
          ...savedTypes.filter(t => !DEFAULT_PROVIDER_TYPES.includes(t)),
        ];
        if (allTypes.length > 0) {
          const svc = lists.provider_services || {};
          const missing = allTypes.filter(t => !svc[t]);
          if (missing.length > 0) {
            const seeded = { ...svc };
            missing.forEach(t => { seeded[t] = []; });
            lists.provider_services = seeded;
            // Persist silently so next load is already correct
            updateProviderServices(seeded).catch(() => {});
          }
        }
        setCustomLists(lists);
        // Keep liveProviderTypes in sync for the editor's live-edit experience
        if (lists.provider_type_options?.length) setLiveProviderTypes(lists.provider_type_options);
        // Also seed selectedProviderType if not set
        if (!selectedProviderType && lists.provider_type_options?.length) {
          // will be set lazily in Services section on first render
        }
      }
    } catch {}
  };

  // Default items to pre-populate when a list has never been saved
  const LIST_DEFAULTS = {
    treatment_type_options: ['Initial Consultation','Follow-Up','MRI','Physical Therapy'],
    case_status_options:    ['intake','active','pending','closed','settled','disengage'],
    legal_status_options:   ['Open','Pending','Closed','Denied','Disputed','Disengage'],
    legal_type_options:     ['WC','Indemnity','Medical Only','Third Party','Death','DOL'],
    af_fee_options:         ['25','33','40','Other'],
    benefit_type_options:   ['TIBs','IIBs','SIBs','Lifetime Income','Death Benefits'],
    language_options:       ['English','Spanish','Other'],
    comm_pref_options:      ['Mail Only','E-mail','Phone Call','Text Message'],
    note_type_options:      ['General','Medical','Legal','Follow-Up'],
    provider_type_options:  ['Doctor','Specialist','Physical Therapy','Chiropractor','Hospital','Lab','Imaging'],
    template_category_options: ['Medical','Legal','General','Billing'],
    specialty_options: [
      'Orthopedic Surgery','Neurology','Neurosurgery','Pain Management','Physical Medicine & Rehabilitation',
      'Chiropractic','Physical Therapy','Occupational Therapy','Radiology / Imaging','Emergency Medicine',
      'Internal Medicine','Family Medicine','Orthopedics','Spine Surgery','Sports Medicine',
      'Psychiatry / Psychology','Ophthalmology','Podiatry','Cardiology','Oncology',
    ],
  };

  const openListEditor = (listKey) => {
    setEditingList(listKey);
    // Sync live provider types when opening that editor
    if (listKey === 'provider_type_options') {
      const current = customLists[listKey] || [];
      if (current.length > 0) setLiveProviderTypes(current);
    }
    // Merge defaults with any saved items so built-in options are never lost
    const existing = customLists[listKey] || [];
    const defaults = LIST_DEFAULTS[listKey] || [];
    // Keep defaults first, then append any custom items that aren't already in defaults
    const merged = [
      ...defaults,
      ...existing.filter(item => !defaults.includes(item)),
    ];
    setEditItems(merged.length > 0 ? merged : existing);
    setNewItem('');
    // Load layout map and SOL rules for legal types
    if (listKey === 'legal_type_options') {
      const map = customLists.legal_type_layout_map || {};
      setEditLayoutMap(typeof map === 'object' ? { ...map } : {});
      const rules = customLists.sol_rules || {};
      setEditSolRules(typeof rules === 'object' ? { ...rules } : {});
    }
  };

  const handleAddItem = () => {
    if (!newItem.trim()) return;
    if (editItems.includes(newItem.trim())) return toast.error('Item already exists');
    setEditItems(prev => [...prev, newItem.trim()]);
    // Default new legal types to 'wc' layout and 1yr/TX SOL rule
    if (editingList === 'legal_type_options') {
      setEditLayoutMap(prev => ({ ...prev, [newItem.trim()]: 'wc' }));
      setEditSolRules(prev => ({ ...prev, [newItem.trim()]: { years: 1, state: 'TX' } }));
    }
    setNewItem('');
  };

  const handleRemoveItem = (idx) => {
    const updated = editItems.filter((_, i) => i !== idx);
    setEditItems(updated);
    if (editingList === 'provider_type_options') setLiveProviderTypes(updated);
  };

  const handleMoveItem = (idx, dir) => {
    const items = [...editItems];
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    [items[idx], items[swap]] = [items[swap], items[idx]];
    setEditItems(items);
  };

  const handleSaveList = async () => {
    if (!editingList) return;
    setSavingList(true);
    try {
      const layoutMap = editingList === 'legal_type_options' ? editLayoutMap : undefined;
      const solRules = editingList === 'legal_type_options' ? editSolRules : undefined;
      await updateCustomList(editingList, editItems, layoutMap, solRules);
      // When provider types are saved, auto-seed new types into provider_services
      // Merge defaults + editItems so Services section always has all types
      let newProviderServices = undefined;
      if (editingList === 'provider_type_options') {
        const existing = customLists.provider_services || {};
        newProviderServices = { ...existing };
        const DEFAULT_PROVIDER_TYPES = LIST_DEFAULTS.provider_type_options || [];
        const allTypes = [
          ...DEFAULT_PROVIDER_TYPES,
          ...editItems.filter(t => !DEFAULT_PROVIDER_TYPES.includes(t)),
        ];
        allTypes.forEach(type => { if (!newProviderServices[type]) newProviderServices[type] = []; });
        // Auto-select first provider type if none selected yet
        if (!selectedProviderType && editItems.length > 0) setSelectedProviderType(editItems[0]);
      }
      setCustomLists(prev => ({
        ...prev,
        [editingList]: editItems,
        ...(layoutMap ? { legal_type_layout_map: layoutMap } : {}),
        ...(solRules ? { sol_rules: solRules } : {}),
        ...(newProviderServices ? { provider_services: newProviderServices } : {}),
      }));
      toast.success('List saved');
      if (editingList === 'provider_type_options') setLiveProviderTypes(editItems);
      setEditingList(null);
    } catch {
      toast.error('Failed to save list');
    } finally {
      setSavingList(false);
    }
  };

  const loadTeam = async () => {
    try {
      setLoading(true);
      const res = await getUsers();
      setTeam(res.users || []);
      setSeats(res.seats || {});
      if (isOwnerOrAdmin) {
        const inv = await api.get('/users/invites').catch(() => []);
        setInvites(Array.isArray(inv) ? inv : []);
        const s = await api.get('/users/subscription').catch(() => ({}));
        setSub(s || {});
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.email) return;
    try {
      const res = await api.post('/users/invite', inviteForm);
      toast.success('Invitation sent!');
      if (res.invite_link) {
        navigator.clipboard?.writeText(res.invite_link);
        toast.success('Invite link copied to clipboard');
      }
      setInviteForm({ email: '', role: 'staff' });
      loadTeam();
    } catch (err) {
      toast.error(err?.message || 'Failed to send invite');
    }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await api.put(`/users/${userId}/role`, { role: newRole });
      toast.success('Role updated');
      loadTeam();
    } catch (err) {
      toast.error(err?.message || 'Failed');
    }
  };

  const deactivateUser = async (userId) => {
    setConfirmAction({
      title: 'Deactivate User',
      message: 'Deactivate this user? They will lose access.',
      confirmLabel: 'Deactivate',
      danger: true,
      onConfirm: async () => {
        try {
          await api.put(`/users/${userId}/deactivate`);
          toast.success('User deactivated');
          loadTeam();
        } catch (err) {
          toast.error(err?.message || 'Failed to deactivate');
        }
      },
    });
  };

  const reactivateUser = async (userId) => {
    try {
      await api.put(`/users/${userId}/reactivate`);
      toast.success('User reactivated');
      loadTeam();
    } catch (err) {
      toast.error(err?.message || 'Failed to reactivate');
    }
  };

  // Super Admin actions
  const handleResetPassword = async () => {
    if (!resetPwdUser || !resetPwdValue) return;
    if (resetPwdValue.length < 6) return toast.error('Password must be at least 6 characters');
    try {
      await resetUserPassword(resetPwdUser._id, resetPwdValue);
      toast.success(`Password reset for ${resetPwdUser.first_name} ${resetPwdUser.last_name}`);
      setResetPwdUser(null);
      setResetPwdValue('');
    } catch (err) {
      toast.error(err?.message || 'Failed to reset password');
    }
  };

  const handlePauseUser = async (u) => {
    setConfirmAction({
      title: 'Pause User',
      message: `Pause ${u.first_name} ${u.last_name}? They will lose system access and billing will be paused.`,
      confirmLabel: 'Pause User',
      danger: true,
      onConfirm: async () => {
        try {
          await pauseUser(u._id);
          toast.success('User paused');
          loadTeam();
        } catch (err) {
          toast.error(err?.message || 'Failed to pause user');
        }
      },
    });
  };

  const handleUnpauseUser = async (u) => {
    try {
      await unpauseUser(u._id);
      toast.success('User unpaused');
      loadTeam();
    } catch (err) {
      toast.error(err?.message || 'Failed to unpause user');
    }
  };

  const handleChangeAccess = async (userId, newAccess) => {
    try {
      await changeAccessLevel(userId, newAccess);
      toast.success('Access level updated');
      loadTeam();
    } catch (err) {
      toast.error(err?.message || 'Failed to update access level');
    }
  };

  const cancelInvite = async (inviteId) => {
    try {
      await api.delete(`/users/invites/${inviteId}`);
      toast.success('Invite cancelled');
      loadTeam();
    } catch (err) {
      toast.error(err?.message || 'Failed');
    }
  };

  // Logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      await uploadLogo(file);
      toast.success('Logo uploaded');
      await refreshUser();
    } catch (err) {
      toast.error(err?.message || 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    setLogoUploading(true);
    try {
      await removeLogo();
      toast.success('Logo removed');
      await refreshUser();
    } catch (err) {
      toast.error(err?.message || 'Failed to remove logo');
    } finally {
      setLogoUploading(false);
    }
  };

  //  Save firm settings to DB
  const saveFirmSettings = async () => {
    if (!isOwnerOrAdmin) return;
    if (!firmForm.name.trim()) {
      toast.error('Firm name is required');
      return;
    }

    setFirmSaving(true);
    try {
      await api.put('/users/organization', firmForm);
      toast.success('Firm settings saved');

      // Refresh /auth/me so your UI reflects DB immediately
      await refreshUser();
    } catch (err) {
      toast.error(err?.message || 'Failed to save firm settings');
    } finally {
      setFirmSaving(false);
    }
  };

  return (
    <>
      <Header title="Settings" />
      <div className="app-content">
        <div className="tabs">
          {[
            { key: 'team', label: 'Team & Users' },
            { key: 'subscription', label: 'Subscription' },
            { key: 'firm', label: 'Firm Settings' },
            { key: 'lists', label: 'Custom Lists' },
            ...(isOwnerOrAdmin ? [{ key: 'deletions', label: `Deletion Requests${deletionRequests.length ? ` (${deletionRequests.length})` : ''}` }] : []),
            { key: 'workflows', label: 'Workflows' },
            { key: 'appearance',   label: 'Appearance' },
          ].map(t => (
            <button
              key={t.key}
              className={`tab ${tab === t.key ? 'active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Team Tab ── */}
        {tab === 'team' && (
          <>
            {/* Seat Usage Banner */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
              <div className="stat-card" style={{ flex: 1 }}>
                <span className="stat-label">Seats Used</span>
                <span className="stat-value" style={{ color: 'var(--zeus-gold)' }}>
                  {seats.used || 0} {seats.purchased >= 999 ? '(Unlimited)' : `/ ${seats.purchased || 3}`}
                </span>
              </div>
              <div className="stat-card" style={{ flex: 1 }}>
                <span className="stat-label">Pending Invites</span>
                <span className="stat-value">{seats.pending_invites || 0}</span>
              </div>
              <div className="stat-card" style={{ flex: 1 }}>
                <span className="stat-label">Plan</span>
                <span className="stat-value" style={{ fontSize: '1.2rem', textTransform: 'capitalize' }}>
                  {sub?.plan_name || { trial: 'Free Trial', trialing: 'Free Trial', core: 'Core', pro: 'Pro', elite: 'Elite', enterprise: 'Enterprise', early_bird: 'Early Bird', free: 'Free', starter: 'Starter' }[seats.plan] || (seats.plan ? seats.plan.replace(/_/g, ' ').replace(/\w/g, c => c.toUpperCase()) : 'Trial')}
                </span>
              </div>
              <div className="stat-card" style={{ flex: 1 }}>
                <span className="stat-label">Per Seat</span>
                <span className="stat-value">
                  {!seats.price_per_seat || seats.price_per_seat === 0
                    ? 'Free'
                    : `$${seats.price_per_seat}/mo`}
                </span>
              </div>
            </div>

            {/* Invite Form */}
            {isOwnerOrAdmin && (
              <div className="card" style={{ marginBottom: '24px' }}>
                <div className="card-header"><h3 className="card-title">Invite Team Member</h3></div>
                <form onSubmit={handleInvite} style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ flex: 2, marginBottom: 0 }}>
                    <label className="form-label">Email</label>
                    <input
                      className="form-input"
                      type="email"
                      value={inviteForm.email}
                      onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })}
                      placeholder="colleague@firm.com"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label className="form-label">Role</label>
                    <select
                      className="form-select"
                      value={inviteForm.role}
                      onChange={e => {
                        const r = e.target.value;
                        setInviteForm({ ...inviteForm, role: r, access_level: ROLE_DEFAULT_ACCESS[r] || 'standard' });
                      }}
                    >
                      {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                    </select>
                    {ROLE_DESCRIPTIONS[inviteForm.role] && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.4 }}>
                        {ROLE_DESCRIPTIONS[inviteForm.role]}
                      </div>
                    )}
                  </div>
                  {isSuperAdmin && (
                    <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                      <label className="form-label">Access Level</label>
                      <select
                        className="form-select"
                        value={inviteForm.access_level}
                        onChange={e => setInviteForm({ ...inviteForm, access_level: e.target.value })}
                      >
                        <option value="standard">Standard</option>
                        <option value="admin">Admin</option>
                        <option value="super_admin">Super Admin</option>
                      </select>
                    </div>
                  )}
                  <button type="submit" className="btn btn-primary" style={{ marginBottom: '0' }}>
                    <Mail size={16} /> Send Invite
                  </button>
                </form>
              </div>
            )}

            {/* Pending Invites */}
            {isOwnerOrAdmin && invites.length > 0 && (
              <div className="card" style={{ marginBottom: '24px' }}>
                <div className="card-header"><h3 className="card-title">Pending Invitations</h3></div>
                {invites.map(inv => (
                  <div
                    key={inv._id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 0',
                      borderBottom: '1px solid var(--zeus-border)'
                    }}
                  >
                    <div>
                      <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{inv.email}</span>
                      <span style={{ marginLeft: '8px', ...getRoleBadgeStyle(inv.role) }}>{getRoleLabel(inv.role)}</span>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => cancelInvite(inv._id)} style={{ color: 'var(--status-red)' }}>
                      Cancel
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Team Members */}
            <div className="card">
              <div className="card-header"><h3 className="card-title">Team Members</h3></div>
              {loading ? (
                <div className="loading-spinner"><div className="spinner" /></div>
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th><th>Email</th><th>Role</th><th>Access</th><th>Last Login</th><th>Status</th>
                        {isOwnerOrAdmin && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {team.map(u => (
                        <tr key={u._id} style={{ opacity: u.is_paused ? 0.6 : 1 }}>
                          <td style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                            {u.first_name} {u.last_name}
                            {(u.access_level || 'standard') === 'super_admin' && <Crown size={14} style={{ color: 'var(--zeus-gold)', marginLeft: '6px' }} />}
                          </td>
                          <td>{u.email}</td>
                          <td>
                            {isOwnerOrAdmin && (u._id !== user?._id || isSuperAdmin) ? (
                              <select
                                className="form-select"
                                style={{ padding: '4px 8px', fontSize: '0.8rem', width: 'auto' }}
                                value={u.role}
                                onChange={e => changeRole(u._id, e.target.value)}
                              >
                                {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                                {/* Show legacy role if user currently has one not in ROLES list */}
                                {!ROLES.includes(u.role) && u.role !== 'owner' && (
                                  <option value={u.role}>{getRoleLabel(u.role)}</option>
                                )}
                              </select>
                            ) : (
                              <span style={getRoleBadgeStyle(u.role)}>
                                {getRoleLabel(u.role)}
                              </span>
                            )}
                          </td>
                          {/* Access Level */}
                          <td>
                            {isSuperAdmin && u._id !== user?._id ? (
                              <select
                                className="form-select"
                                style={{ padding: '4px 8px', fontSize: '0.8rem', width: 'auto' }}
                                value={u.access_level || 'standard'}
                                onChange={e => handleChangeAccess(u._id, e.target.value)}
                              >
                                <option value="super_admin">Super Admin</option>
                                <option value="admin">Admin</option>
                                <option value="standard">Standard</option>
                              </select>
                            ) : (
                              <span className="badge" style={{
                                background: (u.access_level || 'standard') === 'super_admin' ? 'rgba(212,168,83,0.12)' :
                                  (u.access_level || 'standard') === 'admin' ? 'rgba(59,130,246,0.12)' : 'rgba(100,116,139,0.12)',
                                color: (u.access_level || 'standard') === 'super_admin' ? 'var(--zeus-gold)' :
                                  (u.access_level || 'standard') === 'admin' ? '#3b82f6' : '#64748b',
                                fontSize: '0.75rem',
                              }}>
                                {(u.access_level || 'standard') === 'super_admin' ? 'Super Admin' :
                                  (u.access_level || 'standard') === 'admin' ? 'Admin' : 'Standard'}
                              </span>
                            )}
                          </td>
                          <td style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                            {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                          </td>
                          <td>
                            {u.is_paused ? (
                              <span className="badge" style={{ background: 'rgba(249,115,22,0.12)', color: '#f97316' }}>Paused</span>
                            ) : (
                              <span className={`badge ${u.is_active ? 'badge-paid' : 'badge-overdue'}`}>
                                {u.is_active ? 'Active' : 'Inactive'}
                              </span>
                            )}
                          </td>
                          {isOwnerOrAdmin && (
                            <td>
                              {u._id !== user?._id && (u.access_level || 'standard') !== 'super_admin' && (
                                <div style={{ display:'flex', gap:'3px', alignItems:'center' }}>
                                  {/* Pause / Unpause (Super Admin only) */}
                                  {isSuperAdmin && (
                                    u.is_paused ? (
                                      <button className="btn btn-ghost btn-sm" onClick={() => handleUnpauseUser(u)} title="Unpause User"
                                        style={{ padding:'4px', color:'#f97316' }}
                                      >
                                        <Unlock size={14} />
                                      </button>
                                    ) : (
                                      <button className="btn btn-ghost btn-sm" onClick={() => handlePauseUser(u)} title="Pause User"
                                        style={{ padding:'4px' }}
                                      >
                                        <Lock size={14} />
                                      </button>
                                    )
                                  )}

                                  {/* Reset Password (Super Admin only) */}
                                  {isSuperAdmin && (
                                    <button className="btn btn-ghost btn-sm" onClick={() => { setResetPwdUser(u); setResetPwdValue(''); }} title="Reset Password"
                                      style={{ padding:'4px' }}
                                    >
                                      <Key size={14} />
                                    </button>
                                  )}

                                  {/* Deactivate / Reactivate */}
                                  {u.is_active ? (
                                    <button className="btn btn-ghost btn-sm" onClick={() => deactivateUser(u._id)} title="Deactivate" style={{ padding:'4px' }}>
                                      <UserX size={14} />
                                    </button>
                                  ) : (
                                    <button className="btn btn-ghost btn-sm" onClick={() => reactivateUser(u._id)} title="Reactivate"
                                      style={{ padding:'4px', color: 'var(--status-green)' }}
                                    >
                                      <UserCheck size={14} />
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Subscription Tab ── */}
        {tab === 'subscription' && (() => {
          const org = user?.organization || {};
          const status = org.subscription_status || 'trialing';
          const plan   = org.subscription_plan   || 'trial';
          const trialDaysLeft = org.trial_ends_at
            ? Math.ceil((new Date(org.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24))
            : null;
          const isTrial     = plan === 'trial' || plan === 'trialing' || status === 'trialing';
          const trialExpired = isTrial && trialDaysLeft !== null && trialDaysLeft <= 0;
          // Use the actual plan name from the DB (via /users/subscription),
          // fall back to a hardcoded map for orgs where the plan slug isn't in plan_templates
          const PLAN_LABEL_FALLBACK = {
            trial: 'Free Trial', trialing: 'Free Trial',
            early_bird: 'Early Bird',
            core: 'Zeus Core', pro: 'Zeus Pro', elite: 'Zeus Elite',
            free: 'Free', starter: 'Starter', enterprise: 'Enterprise',
          };
          const currentPlanName = sub?.plan_name || PLAN_LABEL_FALLBACK[plan] || plan;
          const planLabel = (p) => (p === plan ? currentPlanName : (PLAN_LABEL_FALLBACK[p] || p));
          const statusColor = { active: '#22c55e', trialing: '#3b82f6', past_due: '#ef4444', cancelled: '#9ca3af' };
          const seatsTotal  = org.seats_purchased >= 999 ? '∞' : (org.seats_purchased || seats.purchased || 3);

          return (
            <div>
              <div className="stats-grid" style={{ marginBottom: '24px' }}>
                <div className="stat-card">
                  <span className="stat-label">Current Plan</span>
                  <span className="stat-value">{currentPlanName}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Status</span>
                  <span className="stat-value" style={{ textTransform: 'capitalize', color: statusColor[status] || 'var(--text-primary)' }}>
                    {status}
                  </span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Seats</span>
                  <span className="stat-value">{seats.used || 0} / {seatsTotal}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Pending Invites</span>
                  <span className="stat-value">{seats.pending_invites || 0}</span>
                </div>
              </div>

              {/* Trial active notice */}
              {isTrial && !trialExpired && trialDaysLeft !== null && (
                <div style={{ padding: '14px 16px', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 'var(--radius-md)', marginBottom: '16px' }}>
                  <div style={{ fontWeight: 700, color: trialDaysLeft <= 3 ? '#ef4444' : trialDaysLeft <= 7 ? '#f59e0b' : '#3b82f6', marginBottom: '6px', fontSize: '0.9rem' }}>
                    {trialDaysLeft <= 0 ? 'Trial expires today' : `${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''} left in your free trial`}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: isOwnerOrAdmin ? '10px' : 0 }}>
                    Upgrade now to keep full access when the trial ends.
                  </div>
                  {isOwnerOrAdmin && (
                    <button onClick={() => navigate('/upgrade')}
                      style={{ padding: '7px 16px', background: 'var(--zeus-gold)', color: '#000', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
                      View Plans & Upgrade
                    </button>
                  )}
                </div>
              )}

              {/* Trial expired */}
              {trialExpired && (
                <div style={{ padding: '14px 16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius-md)', marginBottom: '16px' }}>
                  <div style={{ fontWeight: 700, color: '#ef4444', marginBottom: '6px' }}>Your free trial has expired.</div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: isOwnerOrAdmin ? '10px' : 0 }}>
                    {isOwnerOrAdmin ? 'Upgrade to restore full access.' : 'Contact your firm owner to upgrade.'}
                  </div>
                  {isOwnerOrAdmin && (
                    <button onClick={() => navigate('/upgrade')}
                      style={{ padding: '7px 16px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
                      Upgrade Now
                    </button>
                  )}
                </div>
              )}

              {/* Past due */}
              {status === 'past_due' && (
                <div style={{ padding: '14px 16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius-md)', marginBottom: '16px' }}>
                  <div style={{ fontWeight: 700, color: '#ef4444', marginBottom: '4px' }}>Payment past due.</div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                    New records cannot be created until payment is resolved. Contact{' '}
                    <a href="mailto:support@zeuscms.com" style={{ color: 'var(--zeus-gold)' }}>support@zeuscms.com</a>.
                  </div>
                </div>
              )}

              {/* Active plan — show upgrade link for owners not on Elite */}
              {status === 'active' && !isTrial && plan !== 'elite' && plan !== 'enterprise' && isOwnerOrAdmin && (
                <div style={{ padding: '14px 16px', background: 'rgba(212,168,83,0.06)', border: '1px solid rgba(212,168,83,0.2)', borderRadius: 'var(--radius-md)', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    Want more seats, workflows, or OCR?
                  </div>
                  <button onClick={() => navigate('/upgrade')}
                    style={{ padding: '7px 16px', background: 'var(--zeus-gold)', color: '#000', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', flexShrink: 0 }}>
                    View Plans
                  </button>
                </div>
              )}

              {/* ── Feature Grid ── */}
              {(() => {
                // Derive which features this org has based on plan slug + org flags
                const ELITE_PLANS = ['elite', 'enterprise'];
                const PRO_PLUS    = ['pro', 'elite', 'enterprise', 'early_bird'];
                const ANY_PAID    = ['core', 'pro', 'elite', 'enterprise', 'early_bird'];
                const has = (flag, plans = ANY_PAID) =>
                  org[flag] === true || plans.includes(plan);

                const FEATURES = [
                  // ── Core ──────────────────────────────────────
                  {
                    section: 'Core',
                    items: [
                      { icon: '', label: 'Case Management',       desc: 'Full case lifecycle, documents, notes', active: true },
                      { icon: '', label: 'Client Intake',         desc: 'Intake forms, status tracking, conversion', active: true },
                      { icon: '', label: 'Task Management',       desc: 'Tasks, priorities, reminders, assignments', active: true },
                      { icon: '', label: 'Calendar & Events',     desc: 'Hearings, deadlines, treatment appointments', active: true },
                      { icon: '', label: 'Messenger',             desc: 'Internal team messaging', active: true },
                    ],
                  },
                  // ── Medical & Providers ──────────────────────
                  {
                    section: 'Medical & Providers',
                    items: [
                      { icon: '', label: 'Provider Network',      desc: 'Manage treating and referring providers', active: true },
                      { icon: '', label: 'Treatment Tracking',    desc: 'Consultations, PT orders, records', active: true },
                      { icon: '', label: 'Google Maps / Places',  desc: 'Address autocomplete on forms', active: has('has_google_maps', ['pro','elite','enterprise','early_bird']) },
                    ],
                  },
                  // ── Documents ────────────────────────────────
                  {
                    section: 'Documents & Templates',
                    items: [
                      { icon: '', label: 'Letters & Forms',       desc: 'Demand letters, fax covers, templates', active: true },
                      { icon: '', label: 'Document Templates',    desc: 'Custom document builder', active: true },
                      { icon: '', label: 'OCR Document Reader',   desc: 'Extract text from scanned documents', active: org.has_ocr === true || has('has_ocr', ['elite','enterprise','early_bird']) },
                    ],
                  },
                  // ── Reporting & Automation ────────────────────
                  {
                    section: 'Reporting & Automation',
                    items: [
                      { icon: '', label: 'Reports & Analytics',   desc: 'Case reports, exports, custom filters', active: true },
                      { icon: '', label: 'Workflows & Automation', desc: 'Automated triggers and task rules', active: has('has_workflows', ['pro','elite','enterprise','early_bird']) },
                      { icon: '', label: 'API Access',            desc: 'Integrate with third-party tools', active: org.has_api === true || has('has_api', ELITE_PLANS) },
                    ],
                  },
                  // ── Billing ──────────────────────────────────
                  {
                    section: 'Billing',
                    items: [
                      { icon: '', label: 'Billing & Invoicing',   desc: 'Invoices, attorney fees, collections', active: true },
                    ],
                  },
                ];

                return (
                  <div style={{ marginTop: 28 }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                      Your Plan Features
                    </div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 18 }}>
                      Features included in your <strong style={{ color: 'var(--zeus-gold)' }}>{currentPlanName}</strong> plan.
                      {' '}<span style={{ opacity: 0.6 }}>Grayed features are available on higher plans.</span>
                    </div>

                    {FEATURES.map(({ section, items }) => (
                      <div key={section} style={{ marginBottom: 20 }}>
                        {/* Section label */}
                        <div style={{
                          fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em',
                          textTransform: 'uppercase', color: 'var(--zeus-gold)',
                          borderBottom: '1px solid var(--zeus-border)', paddingBottom: 6, marginBottom: 10,
                        }}>
                          {section}
                        </div>

                        {/* Feature cards grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                          {items.map(({ icon, label, desc, active }) => (
                            <div key={label} style={{
                              display: 'flex', alignItems: 'flex-start', gap: 10,
                              padding: '10px 12px', borderRadius: 8,
                              border: `1px solid ${active ? 'var(--zeus-border)' : 'var(--zeus-border)'}`,
                              background: active ? 'var(--zeus-navy-light)' : 'rgba(255,255,255,0.02)',
                              opacity: active ? 1 : 0.4,
                              transition: 'opacity 0.15s',
                            }}>
                              {/* Icon */}
                              <div style={{ fontSize: '1.1rem', lineHeight: 1, flexShrink: 0, marginTop: 1, filter: active ? 'none' : 'grayscale(1)' }}>
                                {icon}
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                  <span style={{ fontSize: '0.84rem', fontWeight: 600, color: active ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                                    {label}
                                  </span>
                                  {active ? (
                                    <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: 'rgba(34,197,94,0.12)', color: '#22c55e', flexShrink: 0 }}>
                                      INCLUDED
                                    </span>
                                  ) : (
                                    <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: 'rgba(100,116,139,0.15)', color: 'var(--text-muted)', flexShrink: 0 }}>
                                      UPGRADE
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                                  {desc}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}

                    {/* Upgrade CTA at bottom if not on top plan */}
                    {isOwnerOrAdmin && plan !== 'elite' && plan !== 'enterprise' && (
                      <div style={{ marginTop: 8, textAlign: 'center', padding: '14px', background: 'rgba(212,168,83,0.05)', borderRadius: 8, border: '1px solid rgba(212,168,83,0.15)' }}>
                        <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                          Want access to all features?{' '}
                        </span>
                        <button onClick={() => navigate('/upgrade')}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--zeus-gold)', fontWeight: 700, fontSize: '0.85rem', padding: 0, textDecoration: 'underline', fontFamily: 'inherit' }}>
                          View upgrade options →
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

            </div>
          );
        })()}

        {/* ── Firm Settings Tab ──  Editable & saved to DB */}
        {tab === 'firm' && (
          <div className="card" style={{ maxWidth: '650px' }}>
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 className="card-title">Firm Information</h3>
              {isOwnerOrAdmin && (
                <button
                  className="btn btn-primary"
                  onClick={saveFirmSettings}
                  disabled={firmSaving}
                >
                  {firmSaving ? 'Saving...' : 'Save Changes'}
                </button>
              )}
            </div>

            {/* Logo Upload */}
            <div style={{ marginBottom:'20px', padding:'16px', background:'rgba(255,255,255,0.04)', border:'1px solid var(--zeus-border)', borderRadius:'var(--radius-md)' }}>
              <label style={{ fontSize:'0.85rem', fontWeight:600, color:'var(--text-primary)', display:'block', marginBottom:'4px' }}>
                <Image size={14} style={{ marginRight:'6px', verticalAlign:'-2px' }} />
                Firm Logo
              </label>
              <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginBottom:'10px' }}>
                Recommended size: 320 x 132 pixels (PNG or SVG with transparent background)
              </div>
              {user?.organization?.logo_url ? (
                <div style={{ display:'flex', alignItems:'center', gap:'16px' }}>
                  <img
                    src={user.organization.logo_url}
                    alt="Firm Logo"
                    style={{ maxHeight:'82px', maxWidth:'200px', width:'auto', objectFit:'contain', borderRadius:'var(--radius-sm)', background:'rgba(255,255,255,0.05)', padding:'8px' }}
                  />
                  {isOwnerOrAdmin && (
                    <div style={{ display:'flex', gap:'8px' }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={logoUploading}
                      >
                        <Upload size={13} /> Replace
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={handleRemoveLogo}
                        disabled={logoUploading}
                        style={{ color:'var(--status-red)' }}
                      >
                        <Trash2 size={13} /> Remove
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <p style={{ fontSize:'0.82rem', color:'var(--text-muted)', marginBottom:'8px' }}>
                    No logo uploaded. {isOwnerOrAdmin ? 'Upload a 320 x 132 pixel logo to replace the default ZeusCMS logo in the sidebar and use it in letter templates.' : ''}
                  </p>
                  {isOwnerOrAdmin && (
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={logoUploading}
                    >
                      <Upload size={13} /> {logoUploading ? 'Uploading...' : 'Upload Logo'}
                    </button>
                  )}
                </div>
              )}
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                style={{ display:'none' }}
                onChange={handleLogoUpload}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Firm Name</label>
              <input
                className="form-input"
                value={firmForm.name}
                onChange={(e) => setFirmForm({ ...firmForm, name: e.target.value })}
                readOnly={!isOwnerOrAdmin}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Address</label>
              {isOwnerOrAdmin ? (
                <AddressAutocomplete
                  value={firmForm.address || ''}
                  onChange={(v) => setFirmForm(f => ({ ...f, address: v }))}
                  onPlaceSelect={({ street, city, state, zip }) => setFirmForm(f => ({
                    ...f,
                    address: street,
                    city:    city  || f.city  || '',
                    state:   state || f.state || '',
                    zip:     zip   || f.zip   || '',
                  }))}
                  placeholder="Firm street address"
                />
              ) : (
                <input className="form-input" value={firmForm.address || ''} readOnly />
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  className="form-input"
                  value={formatPhone(firmForm.phone)}
                  placeholder="(___) ___-____"
                  onChange={(e) => setFirmForm({ ...firmForm, phone: formatPhone(e.target.value) })}
                  readOnly={!isOwnerOrAdmin}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Fax</label>
                <input
                  className="form-input"
                  value={formatPhone(firmForm.fax)}
                  placeholder="(___) ___-____"
                  onChange={(e) => setFirmForm({ ...firmForm, fax: formatPhone(e.target.value) })}
                  readOnly={!isOwnerOrAdmin}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  className="form-input"
                  value={firmForm.email}
                  onChange={(e) => setFirmForm({ ...firmForm, email: e.target.value })}
                  readOnly={!isOwnerOrAdmin}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Website</label>
                <input
                  className="form-input"
                  value={firmForm.website}
                  onChange={(e) => setFirmForm({ ...firmForm, website: e.target.value })}
                  readOnly={!isOwnerOrAdmin}
                />
              </div>
            </div>

            {!isOwnerOrAdmin && (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginTop: '12px' }}>
                Only Owner/Admin can edit firm settings.
              </p>
            )}

            {/* Organization ID — visible to admins and super admins only */}
            {isAdminAccess && (
              <div style={{
                marginTop: '24px', padding: '14px 16px',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid var(--zeus-border)',
                borderRadius: 'var(--radius-md)',
              }}>
                <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>
                  Organization ID
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <code style={{
                    flex: 1, fontSize: '0.82rem', fontFamily: 'monospace',
                    color: 'var(--zeus-gold)', background: 'rgba(212,168,83,0.08)',
                    padding: '8px 12px', borderRadius: 'var(--radius-sm)',
                    border: '1px solid rgba(212,168,83,0.15)',
                    wordBreak: 'break-all', userSelect: 'all',
                  }}>
                    {user?.organization_id || user?.organization?.id || '—'}
                  </code>
                  <button
                    className="btn btn-ghost btn-sm"
                    title="Copy Organization ID"
                    onClick={() => {
                      const orgId = user?.organization_id || user?.organization?.id;
                      if (!orgId) return;
                      navigator.clipboard.writeText(orgId);
                      toast.success('Organization ID copied');
                    }}
                    style={{ flexShrink: 0, padding: '6px 10px' }}
                  >
                    Copy
                  </button>
                </div>
                <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '8px', marginBottom: 0 }}>
                  Use this ID when contacting support or integrating with external services.
                </p>
              </div>
            )}

            {/* Case Monitoring */}
            {isOwnerOrAdmin && (
              <div style={{ marginTop:'20px', padding:'16px', background:'rgba(255,255,255,0.04)', border:'1px solid var(--zeus-border)', borderRadius:'var(--radius-md)' }}>
                <label style={{ fontSize:'0.85rem', fontWeight:600, color:'var(--text-primary)', display:'block', marginBottom:'4px' }}>
                  <AlertTriangle size={14} style={{ marginRight:'6px', verticalAlign:'-2px' }} />
                  Stale Case Detection
                </label>
                <p style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginBottom:'10px' }}>
                  Cases with no notes, tasks, documents, or status changes past this threshold appear as "Stale" on the Dashboard.
                </p>
                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                  <select
                    className="form-select"
                    value={customLists.stale_case_days || '30'}
                    onChange={async (e) => {
                      const val = e.target.value;
                      try {
                        const lists = { ...customLists, stale_case_days: val };
                        await updateCustomList('stale_case_days', val);
                        setCustomLists(lists);
                        toast.success(`Stale case threshold set to ${val} days`);
                      } catch { toast.error('Failed to update threshold'); }
                    }}
                    style={{ width:'140px' }}
                  >
                    <option value="14">14 days</option>
                    <option value="21">21 days</option>
                    <option value="30">30 days</option>
                    <option value="45">45 days</option>
                    <option value="60">60 days</option>
                    <option value="90">90 days</option>
                  </select>
                  <span style={{ fontSize:'0.82rem', color:'var(--text-muted)' }}>of inactivity</span>
                </div>
              </div>
            )}
          </div>
        )}
        {/* ── Custom Lists Tab ── */}
        {tab === 'lists' && (
          <div className="card">
            <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.1rem', marginBottom:'4px' }}>Custom Lists</h3>
            <p style={{ fontSize:'0.85rem', color:'var(--text-muted)', marginBottom:'16px' }}>
              Configure dropdown options used throughout case details. Changes apply to all users in your firm.
            </p>

            {editingList ? (
              /* ── List Editor ── */
              <div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px' }}>
                  <h4 style={{ fontSize:'0.95rem', color:'var(--zeus-gold)' }}>
                    Editing: {LIST_DEFINITIONS.find(d => d.key === editingList)?.label || editingList}
                  </h4>
                  <div style={{ display:'flex', gap:'6px' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditingList(null)}>Cancel</button>
                    <button
                      className="btn btn-ghost btn-sm"
                      title="Sort alphabetically"
                      onClick={() => {
                        setEditItems(prev => {
                          const sorted = [...prev].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
                          return sorted;
                        });
                      }}
                      style={{ display:'flex', alignItems:'center', gap:'4px' }}
                    >
                      A→Z
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={handleSaveList} disabled={savingList}>
                      <Save size={14} /> {savingList ? 'Saving...' : 'Save List'}
                    </button>
                  </div>
                </div>

                {/* Add new item */}
                <div style={{ display:'flex', gap:'8px', marginBottom:'12px' }}>
                  <input
                    className="form-input"
                    style={{ flex:1, padding:'6px 10px', fontSize:'0.85rem' }}
                    placeholder="Add new item..."
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddItem(); }}
                  />
                  <button className="btn btn-primary btn-sm" onClick={handleAddItem}>
                    <Plus size={14} /> Add
                  </button>
                </div>

                {/* Items list */}
                <div style={{ border:'1px solid var(--zeus-border)', borderRadius:'var(--radius-md)', maxHeight:'400px', overflowY:'auto' }}>
                  {editItems.length === 0 ? (
                    <div style={{ padding:'16px', textAlign:'center', color:'var(--text-muted)', fontSize:'0.85rem' }}>
                      No items. Add one above.
                    </div>
                  ) : (
                    editItems.map((item, idx) => (
                      <div key={idx} style={{
                        display:'flex', alignItems:'center', gap:'10px', padding:'8px 12px',
                        borderBottom:'1px solid var(--zeus-border)',
                      }}>
                        <span style={{ flex:1, fontSize:'0.88rem', color:'var(--text-primary)' }}>{item}</span>
                        {/* Layout toggle + SOL rule for legal types */}
                        {editingList === 'legal_type_options' && (
                          <div style={{ display:'flex', gap:'6px', flexShrink:0, alignItems:'center' }}>
                            <div style={{ display:'flex', gap:'2px' }}>
                              <button
                                onClick={() => setEditLayoutMap(p => ({ ...p, [item]: 'wc' }))}
                                style={{
                                  padding:'2px 8px', fontSize:'0.68rem', fontWeight:700, border:'1px solid',
                                  borderRadius:'4px 0 0 4px', cursor:'pointer', transition:'all 0.15s',
                                  background: (editLayoutMap[item] || 'wc') === 'wc' ? 'rgba(59,130,246,0.15)' : 'transparent',
                                  color: (editLayoutMap[item] || 'wc') === 'wc' ? '#3b82f6' : 'var(--text-muted)',
                                  borderColor: (editLayoutMap[item] || 'wc') === 'wc' ? '#3b82f6' : 'var(--zeus-border)',
                                }}
                              >WC</button>
                              <button
                                onClick={() => setEditLayoutMap(p => ({ ...p, [item]: 'pi' }))}
                                style={{
                                  padding:'2px 8px', fontSize:'0.68rem', fontWeight:700, border:'1px solid',
                                  borderRadius:'0 4px 4px 0', cursor:'pointer', transition:'all 0.15s',
                                  background: editLayoutMap[item] === 'pi' ? 'rgba(34,197,94,0.15)' : 'transparent',
                                  color: editLayoutMap[item] === 'pi' ? '#22c55e' : 'var(--text-muted)',
                                  borderColor: editLayoutMap[item] === 'pi' ? '#22c55e' : 'var(--zeus-border)',
                                }}
                              >PI</button>
                            </div>
                            <span style={{ fontSize:'0.65rem', color:'var(--text-muted)' }}>SOL:</span>
                            <input
                              type="number" min={1} max={10}
                              style={{
                                width:'36px', padding:'2px 4px', fontSize:'0.72rem', textAlign:'center',
                                background:'var(--zeus-navy-light)', border:'1px solid var(--zeus-border)',
                                borderRadius:'4px', color:'var(--text-primary)',
                              }}
                              value={(editSolRules[item]?.years ?? 2)}
                              onChange={(e) => setEditSolRules(prev => ({
                                ...prev, [item]: { ...(prev[item] || {}), years: parseInt(e.target.value) || 1 }
                              }))}
                            />
                            <span style={{ fontSize:'0.65rem', color:'var(--text-muted)' }}>yr</span>
                            <input
                              type="text"
                              style={{
                                width:'32px', padding:'2px 4px', fontSize:'0.72rem', textAlign:'center',
                                background:'var(--zeus-navy-light)', border:'1px solid var(--zeus-border)',
                                borderRadius:'4px', color:'var(--text-primary)', textTransform:'uppercase',
                              }}
                              value={(editSolRules[item]?.state ?? 'TX')}
                              maxLength={2}
                              onChange={(e) => setEditSolRules(prev => ({
                                ...prev, [item]: { ...(prev[item] || {}), state: e.target.value.toUpperCase() }
                              }))}
                            />
                          </div>
                        )}
                        <button
                          onClick={() => handleMoveItem(idx, -1)}
                          disabled={idx === 0}
                          style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:'2px', fontSize:'0.75rem', opacity: idx === 0 ? 0.3 : 1 }}
                        >↑</button>
                        <button
                          onClick={() => handleMoveItem(idx, 1)}
                          disabled={idx === editItems.length - 1}
                          style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:'2px', fontSize:'0.75rem', opacity: idx === editItems.length - 1 ? 0.3 : 1 }}
                        >↓</button>
                        <button
                          onClick={() => handleRemoveItem(idx)}
                          style={{ background:'none', border:'none', cursor:'pointer', color:'var(--status-red)', padding:'2px', display:'flex' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
                <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:'8px' }}>
                  {editItems.length} item{editItems.length !== 1 ? 's' : ''}
                </div>
              </div>
            ) : (
              /* ── List Selector with sections ── */
              <div>
                {LIST_DEFINITIONS.reduce((acc, def, idx) => {
                  if (def.isHeader) {
                    // Close previous grid if open, then open a new section
                    if (acc.currentGroup.length > 0) {
                      acc.sections.push(
                        <div key={`grid-${idx}`} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'20px' }}>
                          {acc.currentGroup}
                        </div>
                      );
                      acc.currentGroup = [];
                    }
                    acc.sections.push(
                      <div key={`hdr-${idx}`} style={{
                        fontSize:'0.72rem', fontWeight:700, letterSpacing:'0.07em',
                        textTransform:'uppercase', color:'var(--zeus-gold)',
                        padding:'4px 0 8px', borderBottom:'1px solid var(--zeus-border)',
                        marginBottom:'12px', marginTop: idx === 0 ? 0 : 4,
                      }}>
                        {def.label}
                      </div>
                    );
                  } else {
                    acc.currentGroup.push(
                      <div
                        key={def.key}
                        onClick={() => openListEditor(def.key)}
                        className="card"
                        style={{ padding:'14px', cursor:'pointer', transition:'all 0.15s', borderLeft:'3px solid var(--zeus-gold)' }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'var(--zeus-navy-light)'}
                        onMouseOut={(e) => e.currentTarget.style.background = ''}
                      >
                        <div style={{ fontWeight:600, fontSize:'0.92rem', color:'var(--text-primary)', marginBottom:'3px' }}>
                          {def.label}
                        </div>
                        <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginBottom:'6px' }}>
                          {def.desc}
                        </div>
                        <div style={{ fontSize:'0.75rem', color:'var(--zeus-gold)' }}>
                          {(customLists[def.key] || []).length} items
                        </div>
                      </div>
                    );
                  }
                  // Flush remaining group at end
                  if (idx === LIST_DEFINITIONS.length - 1 && acc.currentGroup.length > 0) {
                    acc.sections.push(
                      <div key={`grid-end`} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                        {acc.currentGroup}
                      </div>
                    );
                  }
                  return acc;
                }, { sections: [], currentGroup: [] }).sections}
              </div>
            )}
          </div>
        )}

        {/* ── Services Offered by Provider Type (shown inside lists tab) ── */}
        {tab === 'lists' && (() => {
          // Merge defaults with saved — same logic as openListEditor — so Services
          // always shows ALL types, not just the ones saved to DB.
          const savedTypes   = customLists.provider_type_options || [];
          const defaultTypes = LIST_DEFAULTS.provider_type_options || [];
          const providerTypes = [
            ...defaultTypes,
            ...savedTypes.filter(t => !defaultTypes.includes(t)),
          ];
          if (providerTypes.length === 0) return null;

          // Always build a complete servicesMap — seed any type not yet in provider_services
          // This is display-only; the DB gets updated on first add/remove for that type
          const rawServicesMap = customLists.provider_services || {};
          const servicesMap = { ...rawServicesMap };
          providerTypes.forEach(type => { if (!servicesMap[type]) servicesMap[type] = []; });

          // Auto-select first type on first render
          const activeType = selectedProviderType || providerTypes[0];
          const activeServices = (servicesMap[activeType] || []);

          // Dot colors cycling through palette
          const DOT_COLORS = ['#22c55e','#ef4444','#3b82f6','#f59e0b','#8b5cf6','#06b6d4','#ec4899','#f97316','#6366f1','#14b8a6'];
          const dotColor = (i) => DOT_COLORS[i % DOT_COLORS.length];

          const addService = async () => {
            const val = newService.trim();
            if (!val) return;
            if (activeServices.includes(val)) { toast.error('Service already exists'); return; }
            const updated = { ...servicesMap, [activeType]: [...activeServices, val] };
            setSavingServices(true);
            try {
              await updateProviderServices(updated);
              setCustomLists(prev => ({ ...prev, provider_services: updated }));
              setNewService('');
              toast.success('Service added');
            } catch { toast.error('Failed to save'); }
            finally { setSavingServices(false); }
          };

          const removeService = async (svc) => {
            const updated = { ...servicesMap, [activeType]: activeServices.filter(s => s !== svc) };
            try {
              await updateProviderServices(updated);
              setCustomLists(prev => ({ ...prev, provider_services: updated }));
              toast.success('Service removed');
            } catch { toast.error('Failed to remove'); }
          };

          return (
            <div className="card" style={{ marginTop: 20 }}>
              {/* Section header */}
              <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px' }}>
                <ListFilter size={16} style={{ color:'var(--zeus-gold)' }} />
                <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.1rem', margin:0 }}>
                  Services Offered by Provider Type
                </h3>
              </div>
              <p style={{ fontSize:'0.85rem', color:'var(--text-muted)', marginBottom:'18px' }}>
                Define the services each provider type can offer. These appear when assigning treatments in case detail.
              </p>

              <div style={{ display:'flex', gap:'16px', minHeight:'320px' }}>

                {/* Left: provider type list */}
                <div style={{
                  width:'220px', flexShrink:0,
                  border:'1px solid var(--zeus-border)', borderRadius:'var(--radius-md)',
                  overflow:'hidden',
                }}>
                  {providerTypes.map((type, i) => {
                    const count = (servicesMap[type] || []).length;
                    const isActive = type === activeType;
                    return (
                      <div
                        key={type}
                        onClick={() => setSelectedProviderType(type)}
                        style={{
                          display:'flex', alignItems:'center', gap:'10px',
                          padding:'10px 14px', cursor:'pointer',
                          background: isActive ? 'rgba(212,175,55,0.08)' : 'transparent',
                          borderLeft: isActive ? '3px solid var(--zeus-gold)' : '3px solid transparent',
                          borderBottom:'1px solid var(--zeus-border)',
                          transition:'all 0.12s',
                        }}
                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--zeus-navy-light)'; }}
                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <span style={{
                          width:10, height:10, borderRadius:'50%', flexShrink:0,
                          background: dotColor(i),
                        }} />
                        <span style={{
                          flex:1, fontSize:'0.87rem', fontWeight: isActive ? 600 : 400,
                          color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                          whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                        }}>
                          {type}
                        </span>
                        <span style={{
                          fontSize:'0.72rem', fontWeight:700, minWidth:18, textAlign:'right',
                          color: count > 0 ? 'var(--zeus-gold)' : 'var(--text-muted)',
                        }}>
                          {count}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Right: services for selected type */}
                <div style={{ flex:1, display:'flex', flexDirection:'column' }}>
                  {/* Header */}
                  <div style={{
                    padding:'10px 16px', background:'var(--zeus-navy-light)',
                    borderRadius:'var(--radius-sm)', marginBottom:'12px',
                    borderLeft:'3px solid var(--zeus-gold)',
                  }}>
                    <div style={{ fontSize:'0.78rem', fontWeight:700, color:'var(--zeus-gold)', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                      {activeType} Services
                    </div>
                  </div>

                  {/* Add input */}
                  <div style={{ display:'flex', gap:'8px', marginBottom:'12px' }}>
                    <input
                      className="form-input"
                      style={{ flex:1, padding:'7px 12px', fontSize:'0.87rem' }}
                      placeholder={`Add new service for ${activeType}...`}
                      value={newService}
                      onChange={e => setNewService(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addService(); }}
                    />
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={addService}
                      disabled={savingServices}
                      style={{ whiteSpace:'nowrap' }}
                    >
                      <Plus size={14} /> Add
                    </button>
                  </div>

                  {/* Services list */}
                  <div style={{
                    flex:1, border:'1px solid var(--zeus-border)',
                    borderRadius:'var(--radius-md)', overflowY:'auto',
                  }}>
                    {activeServices.length === 0 ? (
                      <div style={{ padding:'32px 16px', textAlign:'center', color:'var(--text-muted)', fontSize:'0.85rem' }}>
                        <ListFilter size={28} style={{ marginBottom:'8px', opacity:0.35 }} />
                        <div>No services defined for <strong>{activeType}</strong> yet.</div>
                        <div style={{ fontSize:'0.78rem', marginTop:'4px' }}>Add one above.</div>
                      </div>
                    ) : (
                      activeServices.map((svc, idx) => (
                        <div key={idx} style={{
                          display:'flex', alignItems:'center', gap:'10px',
                          padding:'9px 14px', borderBottom:'1px solid var(--zeus-border)',
                        }}>
                          <span style={{ flex:1, fontSize:'0.88rem', color:'var(--text-primary)' }}>{svc}</span>
                          <button
                            onClick={() => removeService(svc)}
                            style={{ background:'none', border:'none', cursor:'pointer', color:'var(--status-red)', padding:'2px', display:'flex', opacity:0.7 }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))
                    )}
                  </div>

                  {activeServices.length > 0 && (
                    <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:'6px' }}>
                      {activeServices.length} service{activeServices.length !== 1 ? 's' : ''} for {activeType}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Deletion Requests Tab (Admin Only) ── */}
        {tab === 'deletions' && isOwnerOrAdmin && (
          <div className="card">
            <h3 style={{ fontSize:'1.1rem', marginBottom:'4px' }}>Pending Deletion Requests</h3>
            <p style={{ fontSize:'0.85rem', color:'var(--text-muted)', marginBottom:'16px' }}>
              Review and approve or deny case deletion requests from team members.
            </p>

            {loadingRequests ? (
              <div style={{ padding:'30px', textAlign:'center' }}><div className="spinner" /></div>
            ) : deletionRequests.length === 0 ? (
              <div style={{ padding:'30px', textAlign:'center', color:'var(--text-muted)' }}>
                <Check size={32} style={{ marginBottom:'8px', opacity:0.5 }} />
                <p style={{ fontSize:'0.9rem' }}>No pending deletion requests</p>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                {deletionRequests.map(dr => {
                  const caseInfo = dr.case_ref || {};
                  const requestedBy = dr.requested_by || {};
                  const requesterName = `${requestedBy.first_name || ''} ${requestedBy.last_name || ''}`.trim() || requestedBy.email || 'Unknown';
                  const isProcessing = processingId === dr._id;

                  return (
                    <div key={dr._id} style={{
                      padding:'14px', background:'var(--zeus-navy-light)',
                      border:'1px solid var(--zeus-border)', borderRadius:'var(--radius-md)',
                      borderLeft:'3px solid var(--status-red)',
                    }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'12px' }}>
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px' }}>
                            <AlertTriangle size={16} style={{ color:'var(--status-red)', flexShrink:0 }} />
                            <span style={{ fontWeight:600, fontSize:'0.95rem', color:'var(--text-primary)' }}>
                              {dr.case_number || caseInfo.case_number || '—'}
                            </span>
                            <span style={{ fontSize:'0.85rem', color:'var(--text-secondary)' }}>
                              — {dr.client_name || caseInfo.client_name || 'Unnamed'}
                            </span>
                          </div>
                          <div style={{ fontSize:'0.82rem', color:'var(--text-muted)', marginBottom:'4px' }}>
                            Requested by <strong style={{ color:'var(--text-secondary)' }}>{requesterName}</strong>
                            {requestedBy.role && <span> ({requestedBy.role})</span>}
                            {' · '}
                            {dr.createdAt ? new Date(dr.createdAt).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric', hour:'numeric', minute:'2-digit' }) : ''}
                          </div>
                          {dr.reason && (
                            <div style={{
                              fontSize:'0.85rem', color:'var(--text-secondary)', marginTop:'6px',
                              padding:'8px 10px', background:'rgba(0,0,0,0.15)', borderRadius:'var(--radius-sm)',
                              fontStyle:'italic',
                            }}>
                              "{dr.reason}"
                            </div>
                          )}
                        </div>

                        <div style={{ display:'flex', gap:'6px', flexShrink:0 }}>
                          <button
                            className="btn btn-sm"
                            disabled={isProcessing}
                            onClick={() => handleDeny(dr._id)}
                            style={{ background:'transparent', border:'1px solid var(--text-muted)', color:'var(--text-muted)' }}
                          >
                            <X size={14} /> Deny
                          </button>
                          <button
                            className="btn btn-sm"
                            disabled={isProcessing}
                            onClick={() => handleApprove(dr._id)}
                            style={{ background:'var(--status-red)', color:'#fff', border:'none' }}
                          >
                            <Trash2 size={14} /> {isProcessing ? 'Processing...' : 'Approve & Delete'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ marginTop:'12px' }}>
              <button className="btn btn-ghost btn-sm" onClick={loadDeletionRequests}>
                Refresh
              </button>
            </div>
          </div>
        )}

        {/* ── Workflows Tab ── */}
        {tab === 'workflows' && (
          <>
            {wfMode === 'list' ? (
              <div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
                  <div>
                    <h3 style={{ fontSize:'1rem', fontWeight:700, color:'var(--text-primary)', margin:0 }}>Workflow Builder</h3>
                    <p style={{ fontSize:'0.82rem', color:'var(--text-muted)', margin:'4px 0 0' }}>Automate case actions with trigger-based workflows</p>
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={openNewWorkflow} style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                    <Plus size={14} /> New Workflow
                  </button>
                </div>

                {wfLoading ? (
                  <div style={{ textAlign:'center', padding:'40px', color:'var(--text-muted)' }}><div className="spinner" /></div>
                ) : workflows.length === 0 ? (
                  <div className="card" style={{ textAlign:'center', padding:'50px 20px' }}>
                    <GitBranch size={40} style={{ color:'var(--text-muted)', marginBottom:'12px', opacity:0.4 }} />
                    <h3 style={{ color:'var(--text-secondary)', fontSize:'1rem', marginBottom:'6px' }}>No workflows yet</h3>
                    <p style={{ color:'var(--text-muted)', fontSize:'0.85rem', marginBottom:'16px' }}>Create automated workflows to streamline your case management process.</p>
                    <button className="btn btn-primary btn-sm" onClick={openNewWorkflow} style={{ display:'inline-flex', alignItems:'center', gap:'6px' }}>
                      <Plus size={14} /> Create First Workflow
                    </button>
                  </div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                    {workflows.map(wf => {
                      const trigger = TRIGGER_TYPES.find(t => t.value === wf.trigger_type);
                      return (
                        <div key={wf._id} className="card" style={{ padding:'16px 18px', cursor:'pointer', transition:'border-color 0.15s', border:'1px solid var(--zeus-border)' }}
                          onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--zeus-gold)'}
                          onMouseOut={(e) => e.currentTarget.style.borderColor = 'var(--zeus-border)'}
                        >
                          <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'10px', flex:1, minWidth:0 }} onClick={() => openEditWorkflow(wf)}>
                              <div style={{
                                width:'36px', height:'36px', borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'center',
                                background: wf.is_active ? 'rgba(34,197,94,0.12)' : 'rgba(100,116,139,0.12)',
                                color: wf.is_active ? '#22c55e' : '#64748b', flexShrink:0,
                              }}>
                                <Zap size={18} />
                              </div>
                              <div style={{ minWidth:0 }}>
                                <div style={{ fontWeight:700, fontSize:'0.92rem', color:'var(--text-primary)', display:'flex', alignItems:'center', gap:'8px' }}>
                                  {wf.name}
                                  <span style={{
                                    fontSize:'0.68rem', fontWeight:600, padding:'1px 8px', borderRadius:'10px',
                                    background: wf.is_active ? 'rgba(34,197,94,0.12)' : 'rgba(100,116,139,0.12)',
                                    color: wf.is_active ? '#22c55e' : '#64748b',
                                  }}>{wf.is_active ? 'Active' : 'Inactive'}</span>
                                </div>
                                <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:'2px', display:'flex', alignItems:'center', gap:'6px' }}>
                                  {trigger?.icon} {trigger?.label || wf.trigger_type}
                                  {wf.trigger_type === 'condition_check' && wf.trigger_config?.rules?.length > 0 && (
                                    <span style={{ fontSize:'0.7rem', padding:'1px 6px', borderRadius:'8px', background:'rgba(6,182,212,0.12)', color:'#06b6d4' }}>
                                      {wf.trigger_config.rules.length} rule{wf.trigger_config.rules.length !== 1 ? 's' : ''} · {(wf.trigger_config.logic || 'and').toUpperCase()}
                                    </span>
                                  )}
                                  {wf.trigger_type === 'timed_condition' && wf.trigger_config?.field && wf.trigger_config?.days && (
                                    <span style={{ fontSize:'0.7rem', padding:'1px 6px', borderRadius:'8px', background:'rgba(249,115,22,0.12)', color:'#f97316' }}>
                                      {wf.trigger_config.days}d · {(wf.trigger_config.operator || 'is_blank').replace(/_/g,' ')}
                                    </span>
                                  )}
                                  <span style={{ color:'var(--zeus-border)' }}>·</span>
                                  {wf.steps?.length || 0} step{(wf.steps?.length || 0) !== 1 ? 's' : ''}
                                  {wf.steps?.some(s => s.action_type === 'condition') && (
                                    <span style={{ fontSize:'0.7rem', padding:'1px 6px', borderRadius:'8px', background:'rgba(6,182,212,0.12)', color:'#06b6d4' }}>If/Then</span>
                                  )}
                                  {wf.description && (
                                    <>
                                      <span style={{ color:'var(--zeus-border)' }}>·</span>
                                      <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'200px' }}>{wf.description}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div style={{ display:'flex', alignItems:'center', gap:'4px', flexShrink:0 }}>
                              <button className="btn btn-ghost btn-sm" style={{ padding:'5px' }} title={wf.is_active ? 'Deactivate' : 'Activate'} onClick={(e) => { e.stopPropagation(); handleToggleWorkflow(wf); }}>
                                {wf.is_active ? <ToggleRight size={18} style={{ color:'#22c55e' }} /> : <ToggleLeft size={18} style={{ color:'#64748b' }} />}
                              </button>
                              <button className="btn btn-ghost btn-sm" style={{ padding:'5px' }} title="Duplicate" onClick={(e) => { e.stopPropagation(); handleDuplicateWorkflow(wf); }}>
                                <Copy size={14} />
                              </button>
                              <button className="btn btn-ghost btn-sm" style={{ padding:'5px', color:'var(--status-red)' }} title="Delete" onClick={(e) => { e.stopPropagation(); handleDeleteWorkflow(wf); }}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            ) : (
              /* ── WORKFLOW EDITOR ── */
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'18px' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setWfMode('list')} style={{ display:'flex', alignItems:'center', gap:'5px' }}>
                    <ChevronRight size={14} style={{ transform:'rotate(180deg)' }} /> Back
                  </button>
                  <h3 style={{ fontSize:'1rem', fontWeight:700, color:'var(--text-primary)', margin:0, flex:1 }}>
                    {wfEditing ? 'Edit Workflow' : 'New Workflow'}
                  </h3>
                  <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'0.82rem', color:'var(--text-secondary)', cursor:'pointer' }}>
                    {wfForm.is_active ? <ToggleRight size={18} style={{ color:'#22c55e' }} /> : <ToggleLeft size={18} style={{ color:'#64748b' }} />}
                    <span onClick={() => setWfForm(p => ({ ...p, is_active: !p.is_active }))}>{wfForm.is_active ? 'Active' : 'Inactive'}</span>
                  </label>
                  <button className="btn btn-primary btn-sm" onClick={handleSaveWorkflow} disabled={wfSaving} style={{ display:'flex', alignItems:'center', gap:'5px' }}>
                    <Save size={14} /> {wfSaving ? 'Saving...' : 'Save Workflow'}
                  </button>
                </div>

                {/* Name & Description */}
                <div className="card" style={{ padding:'16px 18px', marginBottom:'12px' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                    <div>
                      <label style={{ fontSize:'0.72rem', color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em', display:'block', marginBottom:'4px' }}>Workflow Name *</label>
                      <input className="form-input" value={wfForm.name} onChange={(e) => setWfForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. New WC Case Intake" />
                    </div>
                    <div>
                      <label style={{ fontSize:'0.72rem', color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em', display:'block', marginBottom:'4px' }}>Description</label>
                      <input className="form-input" value={wfForm.description} onChange={(e) => setWfForm(p => ({ ...p, description: e.target.value }))} placeholder="What does this workflow do?" />
                    </div>
                  </div>
                </div>

                {/* Trigger */}
                <div className="card" style={{ padding:'16px 18px', marginBottom:'12px' }}>
                  <label style={{ fontSize:'0.72rem', color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em', display:'block', marginBottom:'10px' }}>
                    <Zap size={12} style={{ display:'inline', verticalAlign:'-2px', marginRight:'4px' }} /> Trigger — When should this workflow run?
                  </label>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:'8px', marginBottom:'12px' }}>
                    {TRIGGER_TYPES.map(t => (
                      <button key={t.value} onClick={() => setWfForm(p => ({ ...p, trigger_type: t.value, trigger_config: {} }))}
                        style={{
                          padding:'10px 12px', borderRadius:'var(--radius-md)', cursor:'pointer', textAlign:'left',
                          background: wfForm.trigger_type === t.value ? 'rgba(212,168,83,0.12)' : 'var(--zeus-navy-light)',
                          border: wfForm.trigger_type === t.value ? '1.5px solid var(--zeus-gold)' : '1px solid var(--zeus-border)',
                          color: wfForm.trigger_type === t.value ? 'var(--zeus-gold)' : 'var(--text-secondary)',
                          transition:'all 0.15s',
                        }}
                      >
                        <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'0.82rem', fontWeight:600, marginBottom:'2px' }}>{t.icon} {t.label}</div>
                        <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{t.desc}</div>
                      </button>
                    ))}
                  </div>

                  {/* Trigger config */}
                  {wfForm.trigger_type === 'status_changed' && (
                    <div style={{ display:'flex', gap:'10px', alignItems:'center', padding:'10px 12px', background:'var(--zeus-navy-light)', borderRadius:'var(--radius-sm)' }}>
                      <label style={{ fontSize:'0.78rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>From:</label>
                      <select className="form-select" style={{ flex:1, fontSize:'0.82rem', padding:'5px 8px' }}
                        value={wfForm.trigger_config.from_status || ''}
                        onChange={(e) => setWfForm(p => ({ ...p, trigger_config: { ...p.trigger_config, from_status: e.target.value } }))}
                      >
                        <option value="">Any status</option>
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                      </select>
                      <ChevronRight size={14} style={{ color:'var(--text-muted)' }} />
                      <label style={{ fontSize:'0.78rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>To:</label>
                      <select className="form-select" style={{ flex:1, fontSize:'0.82rem', padding:'5px 8px' }}
                        value={wfForm.trigger_config.to_status || ''}
                        onChange={(e) => setWfForm(p => ({ ...p, trigger_config: { ...p.trigger_config, to_status: e.target.value } }))}
                      >
                        <option value="">Any status</option>
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                      </select>
                    </div>
                  )}
                  {wfForm.trigger_type === 'case_created' && (
                    <div style={{ display:'flex', gap:'10px', alignItems:'center', padding:'10px 12px', background:'var(--zeus-navy-light)', borderRadius:'var(--radius-sm)' }}>
                      <label style={{ fontSize:'0.78rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>Only for case type:</label>
                      <select className="form-select" style={{ flex:1, fontSize:'0.82rem', padding:'5px 8px' }}
                        value={wfForm.trigger_config.case_type || ''}
                        onChange={(e) => setWfForm(p => ({ ...p, trigger_config: { ...p.trigger_config, case_type: e.target.value } }))}
                      >
                        <option value="">All case types</option>
                        <option value="workers_comp">Workers Compensation</option>
                        <option value="personal_injury">Personal Injury</option>
                        <option value="department_of_labor">Department of Labor</option>
                      </select>
                    </div>
                  )}
                  {wfForm.trigger_type === 'field_changed' && (
                    <div style={{ display:'flex', gap:'10px', alignItems:'center', padding:'10px 12px', background:'var(--zeus-navy-light)', borderRadius:'var(--radius-sm)' }}>
                      <label style={{ fontSize:'0.78rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>When field changes:</label>
                      <select className="form-select" style={{ flex:1, fontSize:'0.82rem', padding:'5px 8px' }}
                        value={wfForm.trigger_config.field_name || ''}
                        onChange={(e) => setWfForm(p => ({ ...p, trigger_config: { ...p.trigger_config, field_name: e.target.value } }))}
                      >
                        <option value="">Select field...</option>
                        {ALL_FIELDS.map(g => (
                          <optgroup key={g.group} label={g.group}>
                            {g.fields.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                  )}
                  {wfForm.trigger_type === 'condition_check' && (
                    <div style={{ padding:'12px', background:'var(--zeus-navy-light)', borderRadius:'var(--radius-sm)' }}>
                      <div style={{ fontSize:'0.78rem', color:'var(--text-secondary)', marginBottom:'8px' }}>
                        Run this workflow when the following conditions are met on case save:
                      </div>
                      {renderConditionRules(
                        wfForm.trigger_config.rules || [{ field: '', operator: 'is_filled', value: '' }],
                        wfForm.trigger_config.logic || 'and',
                        (rules) => setWfForm(p => ({ ...p, trigger_config: { ...p.trigger_config, rules } })),
                        (logic) => setWfForm(p => ({ ...p, trigger_config: { ...p.trigger_config, logic } })),
                      )}
                    </div>
                  )}

                  {wfForm.trigger_type === 'timed_condition' && (
                    <div style={{ padding:'14px', background:'var(--zeus-navy-light)', borderRadius:'var(--radius-sm)' }}>
                      <div style={{ fontSize:'0.78rem', color:'var(--text-secondary)', marginBottom:'12px' }}>
                        Trigger when a field has been in a specific state for a number of business days:
                      </div>

                      {/* Sentence builder: If [field] has been [operator] [value?] for [N] business days */}
                      <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap', padding:'12px 14px', background:'rgba(0,0,0,0.15)', borderRadius:'var(--radius-sm)', marginBottom:'10px' }}>
                        <span style={{ fontSize:'0.82rem', fontWeight:600, color:'var(--text-primary)' }}>If</span>

                        <select className="form-select" style={{ width:'auto', minWidth:'180px', fontSize:'0.82rem', padding:'6px 8px' }}
                          value={wfForm.trigger_config.field || ''}
                          onChange={(e) => setWfForm(p => ({ ...p, trigger_config: { ...p.trigger_config, field: e.target.value, value: '' } }))}
                        >
                          <option value="">Select field...</option>
                          {ALL_FIELDS.map(g => (
                            <optgroup key={g.group} label={g.group}>
                              {g.fields.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                            </optgroup>
                          ))}
                        </select>

                        <span style={{ fontSize:'0.82rem', fontWeight:600, color:'var(--text-primary)' }}>has been</span>

                        <select className="form-select" style={{ width:'auto', minWidth:'140px', fontSize:'0.82rem', padding:'6px 8px' }}
                          value={wfForm.trigger_config.operator || 'is_blank'}
                          onChange={(e) => setWfForm(p => ({ ...p, trigger_config: { ...p.trigger_config, operator: e.target.value } }))}
                        >
                          {OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>

                        {OPERATORS.find(o => o.value === (wfForm.trigger_config.operator || 'is_blank'))?.needsValue && (() => {
                          const tcOpts = getFieldValueOptions(wfForm.trigger_config.field);
                          if (tcOpts && tcOpts.length > 0) {
                            return (
                              <select className="form-select" style={{ width:'auto', minWidth:'140px', fontSize:'0.82rem', padding:'6px 8px' }}
                                value={wfForm.trigger_config.value || ''}
                                onChange={(e) => setWfForm(p => ({ ...p, trigger_config: { ...p.trigger_config, value: e.target.value } }))}
                              >
                                <option value="">Select...</option>
                                {tcOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            );
                          }
                          return (
                            <input className="form-input" style={{ width:'140px', fontSize:'0.82rem', padding:'6px 8px' }}
                              placeholder="Value..."
                              value={wfForm.trigger_config.value || ''}
                              onChange={(e) => setWfForm(p => ({ ...p, trigger_config: { ...p.trigger_config, value: e.target.value } }))}
                            />
                          );
                        })()}

                        <span style={{ fontSize:'0.82rem', fontWeight:600, color:'var(--text-primary)' }}>for</span>

                        <input className="form-input" type="number" min="1" max="365"
                          style={{ width:'65px', fontSize:'0.82rem', padding:'6px 8px', textAlign:'center' }}
                          value={wfForm.trigger_config.days || ''}
                          onChange={(e) => setWfForm(p => ({ ...p, trigger_config: { ...p.trigger_config, days: parseInt(e.target.value) || '' } }))}
                          placeholder="#"
                        />

                        <span style={{ fontSize:'0.82rem', fontWeight:600, color:'var(--text-primary)' }}>business days</span>
                      </div>

                      {/* Business days info */}
                      <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'0.75rem', color:'var(--text-muted)' }}>
                        <Timer size={12} />
                        Business days = Monday–Friday only (excludes weekends)
                      </div>

                      {/* Live preview sentence */}
                      {wfForm.trigger_config.field && wfForm.trigger_config.days && (
                        <div style={{ marginTop:'10px', padding:'8px 12px', background:'rgba(212,168,83,0.08)', border:'1px solid rgba(212,168,83,0.2)', borderRadius:'var(--radius-sm)', fontSize:'0.8rem', color:'var(--zeus-gold)' }}>
                          <strong>Preview:</strong>{' '}
                          If <strong>{FLAT_FIELD_OPTIONS.find(f => f.value === wfForm.trigger_config.field)?.label || wfForm.trigger_config.field}</strong> has
                          been <strong>{OPERATORS.find(o => o.value === (wfForm.trigger_config.operator || 'is_blank'))?.label?.toLowerCase() || 'blank'}</strong>
                          {OPERATORS.find(o => o.value === (wfForm.trigger_config.operator || 'is_blank'))?.needsValue && wfForm.trigger_config.value
                            ? <> "<strong>{(() => {
                                const pvOpts = getFieldValueOptions(wfForm.trigger_config.field);
                                const pvMatch = pvOpts?.find(o => o.value === wfForm.trigger_config.value);
                                return pvMatch?.label || wfForm.trigger_config.value;
                              })()}</strong>"</> : ''}
                          {' '}for <strong>{wfForm.trigger_config.days}</strong> business day{wfForm.trigger_config.days !== 1 ? 's' : ''}
                          {' '}→ run steps below
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Steps */}
                <div className="card" style={{ padding:'16px 18px' }}>
                  <label style={{ fontSize:'0.72rem', color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em', display:'block', marginBottom:'12px' }}>
                    Steps — What should happen?
                  </label>

                  {wfForm.steps.length > 0 && (
                    <div style={{ marginBottom:'16px' }}>
                      {wfForm.steps.map((step, idx) => {
                        const actionDef = ACTION_TYPES.find(a => a.value === step.action_type);
                        return (
                          <div key={idx}>
                            {idx > 0 && (
                              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'4px 0' }}>
                                <ArrowDown size={16} style={{ color:'var(--text-muted)', opacity:0.5 }} />
                              </div>
                            )}
                            <div style={{
                              padding:'12px 14px', borderRadius:'var(--radius-md)',
                              border:`1.5px solid ${actionDef?.color || 'var(--zeus-border)'}33`,
                              background:`${actionDef?.color || '#64748b'}08`,
                            }}>
                              <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px' }}>
                                <span style={{
                                  width:'22px', height:'22px', borderRadius:'6px', display:'flex', alignItems:'center', justifyContent:'center',
                                  background:`${actionDef?.color || '#64748b'}20`, color: actionDef?.color || '#64748b', fontSize:'0.7rem', fontWeight:700,
                                }}>{idx + 1}</span>
                                <span style={{ color: actionDef?.color || 'var(--text-secondary)', display:'flex', alignItems:'center', gap:'5px', fontWeight:600, fontSize:'0.85rem' }}>
                                  {actionDef?.icon} {actionDef?.label || step.action_type}
                                </span>
                                <div style={{ marginLeft:'auto', display:'flex', gap:'2px' }}>
                                  <button className="btn btn-ghost btn-sm" style={{ padding:'3px' }} onClick={() => moveStep(idx, -1)} disabled={idx === 0} title="Move up"><ArrowUp size={12} /></button>
                                  <button className="btn btn-ghost btn-sm" style={{ padding:'3px' }} onClick={() => moveStep(idx, 1)} disabled={idx === wfForm.steps.length - 1} title="Move down"><ArrowDown size={12} /></button>
                                  <button className="btn btn-ghost btn-sm" style={{ padding:'3px', color:'var(--status-red)' }} onClick={() => removeStep(idx)} title="Remove"><X size={12} /></button>
                                </div>
                              </div>

                              {/* Step config forms */}
                              {step.action_type === 'change_status' && (
                                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                                  <label style={{ fontSize:'0.78rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>Set status to:</label>
                                  <select className="form-select" style={{ flex:1, fontSize:'0.82rem', padding:'5px 8px' }}
                                    value={step.config.new_status || ''} onChange={(e) => updateStep(idx, { new_status: e.target.value })}
                                  >
                                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                                  </select>
                                </div>
                              )}

                              {step.action_type === 'add_note' && (
                                <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                                  <div style={{ display:'flex', gap:'8px' }}>
                                    <div style={{ flex:1 }}>
                                      <label style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Note Type</label>
                                      <select className="form-select" style={{ fontSize:'0.82rem', padding:'5px 8px' }}
                                        value={step.config.note_type || 'General'} onChange={(e) => updateStep(idx, { note_type: e.target.value })}
                                      >
                                        {(customLists.note_type_options || ['General','Phone Call','Email','Meeting','Follow-Up','Internal']).map(t => <option key={t} value={t}>{t}</option>)}
                                      </select>
                                    </div>
                                  </div>
                                  <div>
                                    <label style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Content</label>
                                    <textarea className="form-textarea" style={{ fontSize:'0.82rem', minHeight:'50px', padding:'6px 8px' }}
                                      value={step.config.content || ''} onChange={(e) => updateStep(idx, { content: e.target.value })}
                                      placeholder="Note content... (supports {{case_number}}, {{client_name}} placeholders)"
                                    />
                                  </div>
                                </div>
                              )}

                              {step.action_type === 'send_notification' && (
                                <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                                  <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                                    <label style={{ fontSize:'0.78rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>Send to:</label>
                                    <select className="form-select" style={{ flex:1, fontSize:'0.82rem', padding:'5px 8px' }}
                                      value={step.config.to_role || 'attorney'} onChange={(e) => updateStep(idx, { to_role: e.target.value })}
                                    >
                                      {ROLE_OPTIONS_WF.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}
                                    </select>
                                  </div>
                                  <div>
                                    <label style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Message</label>
                                    <input className="form-input" style={{ fontSize:'0.82rem' }}
                                      value={step.config.message || ''} onChange={(e) => updateStep(idx, { message: e.target.value })}
                                      placeholder="Notification message..."
                                    />
                                  </div>
                                </div>
                              )}

                              {step.action_type === 'assign_user' && (
                                <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                                  <label style={{ fontSize:'0.78rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>Assign to:</label>
                                  <select className="form-select" style={{ flex:1, fontSize:'0.82rem', padding:'5px 8px' }}
                                    value={step.config.assign_to_user || ''} onChange={(e) => updateStep(idx, { assign_to_user: e.target.value })}
                                  >
                                    <option value="">Select user...</option>
                                    {team.filter(u => u.is_active).map(u => <option key={u._id} value={u._id}>{u.first_name} {u.last_name} ({u.role})</option>)}
                                  </select>
                                </div>
                              )}

                              {step.action_type === 'assign_role' && (
                                <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                                  <label style={{ fontSize:'0.78rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>Assign to role:</label>
                                  <select className="form-select" style={{ flex:1, fontSize:'0.82rem', padding:'5px 8px' }}
                                    value={step.config.assign_to_role || 'attorney'} onChange={(e) => updateStep(idx, { assign_to_role: e.target.value })}
                                  >
                                    {ROLE_OPTIONS_WF.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}
                                  </select>
                                </div>
                              )}

                              {step.action_type === 'create_task' && (
                                <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                                    <div>
                                      <label style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Task Title</label>
                                      <input className="form-input" style={{ fontSize:'0.82rem' }}
                                        value={step.config.title || ''} onChange={(e) => updateStep(idx, { title: e.target.value })}
                                        placeholder="e.g. Send LOR to carrier"
                                      />
                                    </div>
                                    <div style={{ display:'flex', gap:'8px' }}>
                                      <div style={{ flex:1 }}>
                                        <label style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Due in (days)</label>
                                        <input className="form-input" type="number" style={{ fontSize:'0.82rem' }}
                                          value={step.config.due_in_days ?? 7} onChange={(e) => updateStep(idx, { due_in_days: parseInt(e.target.value) || 0 })}
                                        />
                                      </div>
                                      <div style={{ flex:1 }}>
                                        <label style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Assign to</label>
                                        <select className="form-select" style={{ fontSize:'0.82rem', padding:'5px 8px' }}
                                          value={step.config.assign_to_role || 'paralegal'} onChange={(e) => updateStep(idx, { assign_to_role: e.target.value })}
                                        >
                                          {ROLE_OPTIONS_WF.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}
                                        </select>
                                      </div>
                                    </div>
                                  </div>
                                  <div>
                                    <label style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Description</label>
                                    <textarea className="form-textarea" style={{ fontSize:'0.82rem', minHeight:'40px', padding:'6px 8px' }}
                                      value={step.config.description || ''} onChange={(e) => updateStep(idx, { description: e.target.value })}
                                      placeholder="Task details..."
                                    />
                                  </div>
                                </div>
                              )}

                              {step.action_type === 'wait' && (
                                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                                  <label style={{ fontSize:'0.78rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>Wait for:</label>
                                  <input className="form-input" type="number" min={1} style={{ width:'80px', fontSize:'0.82rem' }}
                                    value={step.config.days ?? 1} onChange={(e) => updateStep(idx, { days: parseInt(e.target.value) || 1 })}
                                  />
                                  <span style={{ fontSize:'0.82rem', color:'var(--text-muted)' }}>day{(step.config.days ?? 1) !== 1 ? 's' : ''} before next step</span>
                                </div>
                              )}

                              {step.action_type === 'condition' && (
                                <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                                  <div style={{ fontSize:'0.78rem', color:'var(--text-secondary)', fontStyle:'italic', marginBottom:'2px' }}>
                                    Evaluate conditions and branch into different actions:
                                  </div>
                                  {renderConditionRules(
                                    step.config.rules || [{ field:'', operator:'is_filled', value:'' }],
                                    step.config.logic || 'and',
                                    (rules) => updateStep(idx, { rules }),
                                    (logic) => updateStep(idx, { logic }),
                                  )}
                                  {renderBranchActions(
                                    step.config.then_actions || [],
                                    (then_actions) => updateStep(idx, { then_actions }),
                                    ' Then — Do these if conditions pass',
                                    '#22c55e',
                                  )}
                                  {renderBranchActions(
                                    step.config.else_actions || [],
                                    (else_actions) => updateStep(idx, { else_actions }),
                                    ' Else — Do these if conditions fail',
                                    '#ef4444',
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Add step buttons */}
                  <div>
                    <label style={{ fontSize:'0.72rem', color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em', display:'block', marginBottom:'8px' }}>
                      <Plus size={11} style={{ display:'inline', verticalAlign:'-2px', marginRight:'3px' }} /> Add Step
                    </label>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                      {ACTION_TYPES.map(a => (
                        <button key={a.value} onClick={() => addStep(a.value)}
                          className="btn btn-ghost btn-sm"
                          style={{
                            display:'flex', alignItems:'center', gap:'5px', padding:'6px 12px',
                            border:`1px solid ${a.color}44`, color: a.color, fontSize:'0.78rem', fontWeight:600,
                            borderRadius:'var(--radius-md)', transition:'all 0.15s',
                          }}
                          onMouseOver={(e) => { e.currentTarget.style.background = `${a.color}12`; e.currentTarget.style.borderColor = a.color; }}
                          onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = `${a.color}44`; }}
                        >
                          {a.icon} {a.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Appearance Tab ── */}
        {tab === 'appearance' && (
          <div className="card" style={{ maxWidth: '650px' }}>
            <div className="card-header">
              <h3 className="card-title">Appearance</h3>
            </div>

            <div className="theme-toggle-row">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {theme === 'dark' ? (
                  <Moon size={20} style={{ color: 'var(--zeus-gold)' }} />
                ) : (
                  <Sun size={20} style={{ color: 'var(--zeus-gold)' }} />
                )}
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                    {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                    {theme === 'dark' ? 'Switch to light mode for a brighter interface' : 'Switch to dark mode for reduced eye strain'}
                  </div>
                </div>
              </div>

              <label className="theme-switch">
                <input
                  type="checkbox"
                  checked={theme === 'light'}
                  onChange={toggleTheme}
                />
                <span className="theme-slider" />
              </label>
            </div>

            <div style={{ marginTop: '16px', padding: '14px', borderRadius: 'var(--radius-md)', background: 'var(--zeus-navy-light)', border: '1px solid var(--zeus-border)' }}>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                Preview: Your current theme is <strong style={{ color: 'var(--zeus-gold)' }}>{theme === 'dark' ? 'Dark' : 'Light'}</strong>. 
                This preference is saved to your browser and will persist across sessions.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Reset Password Modal (Super Admin) */}
      {resetPwdUser && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setResetPwdUser(null)}
        >
          <div style={{ background:'var(--zeus-navy)', border:'1px solid var(--zeus-border)', borderRadius:'var(--radius-md)', padding:'24px', width:'380px', maxWidth:'90vw' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin:'0 0 12px', fontSize:'1rem', color:'var(--text-primary)' }}>
              <Key size={16} style={{ display:'inline', verticalAlign:'-2px', marginRight:'6px', color:'var(--zeus-gold)' }} />
              Reset Password
            </h3>
            <p style={{ fontSize:'0.82rem', color:'var(--text-muted)', margin:'0 0 14px' }}>
              Set a new password for <strong style={{ color:'var(--text-primary)' }}>{resetPwdUser.first_name} {resetPwdUser.last_name}</strong> ({resetPwdUser.email})
            </p>
            <input
              className="form-input" type="password" placeholder="New password (min 6 characters)"
              value={resetPwdValue} onChange={(e) => setResetPwdValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleResetPassword(); }}
              autoFocus
            />
            <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'14px' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setResetPwdUser(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleResetPassword} disabled={!resetPwdValue || resetPwdValue.length < 6}>
                <Key size={14} /> Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={async () => { await confirmAction?.onConfirm(); setConfirmAction(null); }}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmLabel={confirmAction?.confirmLabel || 'Confirm'}
        danger={confirmAction?.danger !== false}
      />
    </>
  );
}